/**
 * Screen-specific theme configurations
 *
 * This file maps color groups to screen-specific theme objects.
 * Each screen has light/dark variants that reference the color groups.
 *
 * STRUCTURE:
 * - Import color groups from colorGroups/
 * - Map color properties to screen-specific theme keys
 * - Maintain light/dark separation for all screens
 */

import {commonColors} from './colorGroups/common.colors';
import {createFlowColors} from './colorGroups/createFlow.colors';
import {loginColors} from './colorGroups/login.colors';
import {
  buttonColors,
  inputColors,
  checkboxColors,
  dropdownColors,
  searchInputColors,
  calenderColors,
  uiColors,
  profileColors,
  errorToast,
  feedback,
  ModalColors,
} from './colorGroups/ui.colors';
import {
  eyeColors,
  helpColors,
  languageColors,
  profileMenuColors,
} from './colorGroups/icons.colors';
import {
  forgotPasswordColors,
  securityQuestionColors,
  passwordValidationColors,
  pinAuthColors,
  pinInputColors,
  logout,
} from './colorGroups/auth.colors';
import {workOrderColors} from './colorGroups/workOrder.colors';
import {
  scoreBoardColors,
  statusChartColors,
  statusPieChartColors,
  leaderBoardColors,
  leaderRankColors,
  repairBoardColors,
  dashboardColors,
} from './colorGroups/dashboard.colors';
import {
  listViewColors,
  listPaginationColors,
  priorityColors,
  statusColors,
  tileViewColors,
  partsStatusColors,
} from './colorGroups/listView.colors';
import {shopHomeColors} from './colorGroups/shopHome.colors';
import {novaAIColors} from './colorGroups/novaAI.colors';
import {
  menuLayoutColors,
  mainLayoutColors,
  indirectActivityColors,
  selectedShopColors,
  quickAssitColors,
} from './colorGroups/menu.colors';
import {
  technicianStatusColors,
  shiftStatusColors,
  beginShiftColors,
  endShiftColors,
  detailColors,
  viewAllColors,
} from './colorGroups/misc.colors';
import {repairDetailsColors} from './colorGroups/repairDetails.colors';
import {tooltipColors} from './colorGroups/tooltip.colors';
import {helperColors} from './colorGroups/help.colors';

export const screenThemes = {
  loginScreen: {
    light: {
      mainBackgroundColor: loginColors.mainBGColor.light,
      backgroundColor: loginColors.contentBGColor.light,
      borderColor: loginColors.pageBorder.light,
      textColor: loginColors.textColor.light,
      inputBorder: inputColors.border.light,
      inputBG: inputColors.BG.light,
      forgotPassWordText: loginColors.forgotPassWordText.light,
      headerText: loginColors.headerText.light,
      dotsColor: loginColors.dotsColor.light,
      actionsTextColor: loginColors.actionsTextColor.light,
      helpText: helpColors.text.light,
      helpBorder: helpColors.border.light,
      openEyeIcon: eyeColors.openIcon.light,
      closeEyeIcon: eyeColors.closeIcon.light,
      helpIcon: helpColors.icon.light,
      languageIcon: languageColors.icon.light,
      dropdownIcon: dropdownColors.icon.light,
      loginPageSelectedTextColor: loginColors.pageSelectedTextColor.light,
      loginShadowColor: loginColors.shadowColor.light,
      formLabel: createFlowColors.formLabel.light,
      verifyBtnDisabledBG: createFlowColors.verifyBtnDisabledBG.light,
      verifyBtnDisabledText: createFlowColors.verifyBtnDisabledText.light,
      verifyBtnEnabledBG: createFlowColors.verifyBtnEnabledBG.light,
      verifyBtnEnabledText: createFlowColors.verifyBtnEnabledText.light,
      headerModalText: loginColors.headerModalText.light
    },
    dark: {
      mainBackgroundColor: loginColors.mainBGColor.dark,
      backgroundColor: loginColors.contentBGColor.dark,
      borderColor: loginColors.pageBorder.dark,
      textColor: loginColors.textColor.dark,
      inputBorder: inputColors.border.dark,
      inputBG: inputColors.BG.dark,
      forgotPassWordText: loginColors.forgotPassWordText.dark,
      headerText: loginColors.headerText.dark,
      dotsColor: loginColors.dotsColor.dark,
      actionsTextColor: loginColors.actionsTextColor.dark,
      helpText: helpColors.text.dark,
      helpBorder: helpColors.border.dark,
      openEyeIcon: eyeColors.openIcon.dark,
      closeEyeIcon: eyeColors.closeIcon.dark,
      helpIcon: helpColors.icon.dark,
      languageIcon: languageColors.icon.dark,
      dropdownIcon: dropdownColors.icon.dark,
      loginPageSelectedTextColor: loginColors.pageSelectedTextColor.dark,
      loginShadowColor: loginColors.shadowColor.dark,
      formLabel: createFlowColors.formLabel.dark,
      verifyBtnDisabledBG: createFlowColors.verifyBtnDisabledBG.dark,
      verifyBtnDisabledText: createFlowColors.verifyBtnDisabledText.dark,
      verifyBtnEnabledBG: createFlowColors.verifyBtnEnabledBG.dark,
      verifyBtnEnabledText: createFlowColors.verifyBtnEnabledText.dark,
      headerModalText: loginColors.headerModalText.dark
    },
  },

  forgotPassword: {
    light: {
      mainBackgroundColor: commonColors.white.light,
      backgroundColor: 'transparent',
      borderColor: loginColors.pageBorder.light,
      textColor: loginColors.textColor.light,
      inputBorder: inputColors.border.light,
      inputBG: inputColors.BG.light,
      forgotPassWordText: loginColors.forgotPassWordText.light,
      headerText: loginColors.headerText.light,
      dotsColor: loginColors.dotsColor.light,
      actionsTextColor: loginColors.actionsTextColor.light,
      helpText: helpColors.text.light,
      helpBorder: helpColors.border.light,
      headerSubText: forgotPasswordColors.subHeaderText.light,
      backCTAColor: forgotPasswordColors.backCTAColor.light,
      backIcon: forgotPasswordColors.backIcon.light,
      helpIcon: helpColors.icon.light,
    },
    dark: {
      mainBackgroundColor: commonColors.black.dark,
      backgroundColor: loginColors.pageBackground.dark,
      borderColor: loginColors.pageBorder.dark,
      textColor: loginColors.textColor.dark,
      inputBorder: inputColors.border.dark,
      inputBG: inputColors.BG.dark,
      forgotPassWordText: loginColors.forgotPassWordText.dark,
      headerText: loginColors.headerText.dark,
      dotsColor: loginColors.dotsColor.dark,
      actionsTextColor: loginColors.actionsTextColor.dark,
      helpText: helpColors.text.dark,
      helpBorder: helpColors.border.dark,
      headerSubText: forgotPasswordColors.subHeaderText.dark,
      backCTAColor: forgotPasswordColors.backCTAColor.dark,
      backIcon: forgotPasswordColors.backIcon.dark,
      helpIcon: helpColors.icon.dark,
    },
  },

  securityQuestion: {
    light: {
      mainBackgroundColor: commonColors.white.light,
      backgroundColor: 'transparent',
      borderColor: loginColors.pageBorder.light,
      textColor: loginColors.textColor.light,
      inputBorder: inputColors.border.light,
      inputBG: inputColors.BG.light,
      forgotPassWordText: loginColors.forgotPassWordText.light,
      headerText: loginColors.headerText.light,
      dotsColor: loginColors.dotsColor.light,
      actionsTextColor: loginColors.actionsTextColor.light,
      helpText: helpColors.text.light,
      helpBorder: helpColors.border.light,
      headerSubText: forgotPasswordColors.subHeaderText.light,
      backCTAColor: forgotPasswordColors.backCTAColor.light,
      backIcon: forgotPasswordColors.backIcon.light,
      helpIcon: helpColors.icon.light,
      languageIcon: languageColors.icon.light,
      dropdownIcon: dropdownColors.icon.light,
      securityQuestionBG: securityQuestionColors.BG.light,
      securityQuestionBorder: securityQuestionColors.border.light,
      securityQuestionIcon: securityQuestionColors.icon.light,
      securityQuestionIconBG: securityQuestionColors.iconBG.light,
      securityQuestionText: securityQuestionColors.text.light,
      securityQuestionSubText: securityQuestionColors.subText.light,
    },
    dark: {
      mainBackgroundColor: commonColors.black.dark,
      backgroundColor: loginColors.pageBackground.dark,
      borderColor: loginColors.pageBorder.dark,
      textColor: loginColors.textColor.dark,
      inputBorder: inputColors.border.dark,
      inputBG: inputColors.BG.dark,
      forgotPassWordText: loginColors.forgotPassWordText.dark,
      headerText: loginColors.headerText.dark,
      dotsColor: loginColors.dotsColor.dark,
      actionsTextColor: loginColors.actionsTextColor.dark,
      helpText: helpColors.text.dark,
      helpBorder: helpColors.border.dark,
      headerSubText: forgotPasswordColors.subHeaderText.dark,
      backCTAColor: forgotPasswordColors.backCTAColor.dark,
      backIcon: forgotPasswordColors.backIcon.dark,
      helpIcon: helpColors.icon.dark,
      languageIcon: languageColors.icon.dark,
      dropdownIcon: dropdownColors.icon.dark,
      securityQuestionBG: securityQuestionColors.BG.dark,
      securityQuestionBorder: securityQuestionColors.border.dark,
      securityQuestionIcon: securityQuestionColors.icon.dark,
      securityQuestionIconBG: securityQuestionColors.iconBG.dark,
      securityQuestionText: securityQuestionColors.text.dark,
      securityQuestionSubText: securityQuestionColors.subText.dark,
    },
  },

  createNewPassword: {
    light: {
      mainBackgroundColor: commonColors.white.light,
      backgroundColor: 'transparent',
      borderColor: loginColors.pageBorder.light,
      textColor: loginColors.textColor.light,
      inputBorder: inputColors.border.light,
      inputBG: inputColors.BG.light,
      forgotPassWordText: loginColors.forgotPassWordText.light,
      headerText: loginColors.headerText.light,
      dotsColor: loginColors.dotsColor.light,
      actionsTextColor: loginColors.actionsTextColor.light,
      helpText: helpColors.text.light,
      helpBorder: helpColors.border.light,
      headerSubText: forgotPasswordColors.subHeaderText.light,
      backCTAColor: forgotPasswordColors.backCTAColor.light,
      backIcon: forgotPasswordColors.backIcon.light,
      helpIcon: helpColors.icon.light,
      languageIcon: languageColors.icon.light,
      dropdownIcon: dropdownColors.icon.light,
      openEyeIcon: eyeColors.openIcon.light,
      closeEyeIcon: eyeColors.closeIcon.light,
      passwordValidationListBg: passwordValidationColors.listBg.light,
      passwordValidationListBorder: passwordValidationColors.listBorder.light,
      passwordValidationListColor: securityQuestionColors.text.light,
      passWordvalidationTickIcon: passwordValidationColors.tickIcon.light,
    },
    dark: {
      mainBackgroundColor: commonColors.black.dark,
      backgroundColor: loginColors.pageBackground.dark,
      borderColor: loginColors.pageBorder.dark,
      textColor: loginColors.textColor.dark,
      inputBorder: inputColors.border.dark,
      inputBG: inputColors.BG.dark,
      forgotPassWordText: loginColors.forgotPassWordText.dark,
      headerText: loginColors.headerText.dark,
      dotsColor: loginColors.dotsColor.dark,
      actionsTextColor: loginColors.actionsTextColor.dark,
      helpText: helpColors.text.dark,
      helpBorder: helpColors.border.dark,
      headerSubText: forgotPasswordColors.subHeaderText.dark,
      backCTAColor: forgotPasswordColors.backCTAColor.dark,
      backIcon: forgotPasswordColors.backIcon.dark,
      helpIcon: helpColors.icon.dark,
      languageIcon: languageColors.icon.dark,
      dropdownIcon: dropdownColors.icon.dark,
      openEyeIcon: eyeColors.openIcon.dark,
      closeEyeIcon: eyeColors.closeIcon.dark,
      passwordValidationListBg: passwordValidationColors.listBg.dark,
      passwordValidationListBorder: passwordValidationColors.listBorder.dark,
      passwordValidationListColor: securityQuestionColors.text.dark,
      passWordvalidationTickIcon: passwordValidationColors.tickIcon.dark,
    },
  },

  passwordUpdated: {
    light: {
      mainBackgroundColor: commonColors.white.light,
      backgroundColor: 'transparent',
      borderColor: loginColors.pageBorder.light,
      headerText: loginColors.headerText.light,
      headerSubText: forgotPasswordColors.subHeaderText.light,
      actionsTextColor: loginColors.actionsTextColor.light,
      helpIcon: helpColors.icon.light,
      helpBorder: helpColors.border.light,
      helpText: helpColors.text.light,
      backCTAColor: forgotPasswordColors.backCTAColor.light,
      backIcon: forgotPasswordColors.backIcon.light,
    },
    dark: {
      mainBackgroundColor: commonColors.black.dark,
      backgroundColor: loginColors.pageBackground.dark,
      borderColor: loginColors.pageBorder.dark,
      headerText: loginColors.headerText.dark,
      headerSubText: forgotPasswordColors.subHeaderText.dark,
      actionsTextColor: loginColors.actionsTextColor.dark,
      helpIcon: helpColors.icon.dark,
      helpBorder: helpColors.border.dark,
      helpText: helpColors.text.dark,
      backCTAColor: forgotPasswordColors.backCTAColor.dark,
      backIcon: forgotPasswordColors.backIcon.dark,
    },
  },

  MainLayoutScreen: {
    light: {
      background: commonColors.lightWhite.light,
      menuBg: commonColors.lightWhite.light,
      sidebarBG: commonColors.white.light,
      label: uiColors.menuIconText.light,
      activeLabel: uiColors.selectedIconColor.light,
      border: uiColors.border.light,
      selectedTab: uiColors.selectedTab.light,
      iconColor: uiColors.iconColorAlt.light,
      menuLayoutDivider: menuLayoutColors.divider.light,
      bottomMenuLayoutDivider: menuLayoutColors.bottomDivider.light,
      profileBGColor: profileMenuColors.BGColor.light,
      profileTextColor: profileMenuColors.textColor.light,
      selectedTabBorder: uiColors.selectedTabBorder.light,
      tabBorder: uiColors.tabBorder.light,
      mainLayOutShadowColor: mainLayoutColors.shadowColor.light,
      tabDisabledText: uiColors.tabDisabledText.light,
      tabDisabledBg: uiColors.tabDisabledBg.light,
      tabDisabledIconColor: uiColors.tabDisabledIconColor.light,
      tabHoverBg: uiColors.tabHoverBg.light,
      tabPressedBg: uiColors.tabPressedBg.light,
    },
    dark: {
      background: commonColors.black.dark,
      menuBg: commonColors.black.dark,
      sidebarBG: commonColors.black.dark,
      label: uiColors.menuIconText.dark,
      activeLabel: uiColors.selectedIconColor.dark,
      border: uiColors.border.dark,
      selectedTab: uiColors.selectedTab.dark,
      iconColor: uiColors.iconColorAlt.dark,
      menuLayoutDivider: menuLayoutColors.divider.dark,
      bottomMenuLayoutDivider: menuLayoutColors.bottomDivider.dark,
      profileBGColor: profileMenuColors.BGColor.dark,
      profileTextColor: profileMenuColors.textColor.dark,
      selectedTabBorder: uiColors.selectedTabBorder.dark,
      tabBorder: uiColors.tabBorder.dark,
      mainLayOutShadowColor: mainLayoutColors.shadowColor.dark,
      tabDisabledText: uiColors.tabDisabledText.dark,
      tabDisabledBg: uiColors.tabDisabledBg.dark,
      tabDisabledIconColor: uiColors.tabDisabledIconColor.dark,
      tabHoverBg: uiColors.tabHoverBg.dark,
      tabPressedBg: uiColors.tabPressedBg.dark,
    },
  },

  DashboardScreen: {
    light: {
      background: commonColors.lightWhite.light,
      text: commonColors.black.light,
      card: commonColors.white.light,
      subText: commonColors.subInterText.light,
      priceTag: commonColors.red.light,
      priceTagTextColor: commonColors.white.light,
      scoreBoardIconBG: scoreBoardColors.iconBG.light,
      scoreBoardIconColor: scoreBoardColors.icon.light,

      tabIconColor: dashboardColors.tabIconColor.light,
      tabTextColor: dashboardColors.tabTextColor.light,
      selectedCountTextColor: dashboardColors.selectedCountTextColor.light,
      countTextColor: dashboardColors.countTextColor.light,
      tabIconSelectedColor: dashboardColors.tabIconSelectedColor.light,
      tabTextSelectedColor: dashboardColors.tabTextSelectedColor.light,
      countBG: dashboardColors.countBG.light,
      selectedCountBG: dashboardColors.selecetdCountBG.light,
      tileCardCTASelectedBorder:
        repairDetailsColors.tileCardCTASelectedBorder.light,

      shiftBGColor: dashboardColors.shiftCardBG.light,
      workOrderCardBG: scoreBoardColors.cardBG.light,
      workOrderCardBorder: scoreBoardColors.cardBorder.light,
      workOrderTitle: workOrderColors.titleColor.light,
      workOrderSortIconsBG: workOrderColors.sortBGAlt.light,
      workOrderSortIconsBorder: workOrderColors.sortBorderAlt.light,
      workOrderTileViewIcon: workOrderColors.tileViewIcon.light,
      workOrdeViewBorder: shopHomeColors.workOrdeViewBorder.light,
      workOrderListViewIconColor: workOrderColors.listViewIconColor.light,
      workOrderTileViewSelectedIcon: workOrderColors.tileViewSelectedIcon.light,
      workOrderListViewSelectedIconColor:
        workOrderColors.listViewSelectedIconColor.light,
      workOrderFilterBorder: workOrderColors.filterBorder.light,
      workOrderFilterSelectedText: workOrderColors.filterSelectedText.light,
      workOrderFilterSelectedTextBorder:
        workOrderColors.filterSelectedTextBorder.light,
      workOrderStatusSelectedTextBG:
        workOrderColors.workOrderStatusSelectedTextBG.light,
      workOrderStatusSelectedText:
        workOrderColors.workOrderStatusSelectedText.light,
      workOrderStatusSelectedTextBorder:
        workOrderColors.workOrderStatusSelectedTextBorder.light,
      workOrderStatusTextBorder:
        workOrderColors.workOrderStatusTextBorder.light,
      workOrderStatusTextBG: workOrderColors.workOrderStatusTextBG.light,
      workOrderStatusText: workOrderColors.workOrderStatusText.light,
      workOrderFilterText: workOrderColors.filterText.light,
      workOrderViewAllCTAText: workOrderColors.viewAllCTAText.light,
      workOrderCardListBorder: workOrderColors.cardBorder.light,
      workOrderCardListTitle: workOrderColors.cardListTitle.light,
      workOrderCardListSubTitle: workOrderColors.cardListSubTitle.light,
      workOrderCardIconBG: workOrderColors.cardIconBG.light,

      workOrderStatusBG: workOrderColors.statusBG.light,
      workOrderStatausBorder: workOrderColors.statausBorder.light,
      workOrderStatusTitle: workOrderColors.statusTitle.light,
      workOrderStatusDate: workOrderColors.statusDate.light,
      workOrderStatusDateTitle: workOrderColors.statusDateTitle.light,
      workOrderStatusDateBG: workOrderColors.statusDateBG.light,
      workOrderStatusDateBorder: workOrderColors.statusDateBorder.light,
      workOrderStatusDateIcon: workOrderColors.statusDateIcon.light,

      statusChartStatusesText: statusChartColors.statusesText.light,
      statusChartStatusesBorder: statusChartColors.statusesBorder.light,
      statusChartStatusesTotalText: statusChartColors.statusesTotalText.light,
      statusChartStatusesSubTotalText:
        statusChartColors.statusesSubTotalText.light,
      statusInprogress: statusChartColors.inprogress.light,
      statusActive: statusChartColors.active.light,
      statusAwaitingParts: statusChartColors.awaitingParts.light,
      statusCompleted: statusChartColors.completed.light,
      statusPieChartOuterBorder: statusPieChartColors.outerBorder.light,
      statusPieChartBorderShadow: statusPieChartColors.borderShadow.light,
      statusPieChartBorder: statusPieChartColors.border.light,
      statusPieChartToolTipBG: statusPieChartColors.toolTipBG.light,
      statusPieChartToolTipText: statusPieChartColors.toolTipText.light,

      leaderBoardBG: leaderBoardColors.BG.light,
      leaderBoardTitle: leaderBoardColors.title.light,
      leaderBoardBorder: leaderBoardColors.border.light,
      leaderBoardSubTitle: leaderBoardColors.subTitle.light,
      leaderBoardCard: leaderBoardColors.cardBG.light,
      leaderBoardCardBorder: leaderBoardColors.cardBorder.light,
      leaderBoardCardLeaderName: leaderBoardColors.cardLeaderName.light,
      leaderBoardCardSelfLeaderName: leaderBoardColors.cardSelfLeaderName.light,
      leaderBoardCardSelfLeaderBG: leaderBoardColors.cardSelfLeaderBG.light,
      leaderBoardCardLeaderRepairs: leaderBoardColors.cardLeaderRepairs.light,
      leaderBoardSelectedCard: leaderBoardColors.selectedCard.light,
      leaderBoardSelectedCardBorder: leaderBoardColors.selectedCardBorder.light,
      leaderBoardRankTextColor: leaderBoardColors.rankTextColor.light,
      leaderBoardBottomLine: leaderBoardColors.bottomLine.light,
      leaderBoardBottomText: leaderBoardColors.bottomText.light,

      repairBoardCardBorder: repairBoardColors.cardBorder.light,

      LeaderOneColorBG: leaderRankColors.oneColorBG.light,
      LeaderOneColorBorder: leaderRankColors.oneColorBorder.light,
      LeaderTwoColorBG: leaderRankColors.twoColorBG.light,
      LeaderTwoColorBorder: leaderRankColors.twoColorBorder.light,
      LeaderThreeColorBG: leaderRankColors.threeColorBG.light,
      LeaderThreeColorBorder: leaderRankColors.threeColorBorder.light,
      LeaderRemainingColorBG: leaderRankColors.remainingColorBG.light,
      LeaderRemainingColorBorder: leaderRankColors.remainingColorBorder.light,
      LeaderRemainingColorText: leaderRankColors.remainingColorText.light,
      LeaderFourIconBG: leaderRankColors.fourIconBG.light,
      LeaderFourIconBorder: leaderRankColors.fourIconBorder.light,

      listViewBG: listViewColors.BG.light,
      listViewBorder: listViewColors.border.light,
      tileViewBorder: tileViewColors.cardBorder.light,
      tileViewSelectedBorder: tileViewColors.cardSelectedBorder.light,
      tileViewSelectedShadow: tileViewColors.cardSelectedShadow.light,
      tileViewSelectedButtonBorder: tileViewColors.cardSelectedButtonBorder.light,
      partsViewBorder: tileViewColors.partsBorder.light,
      partsStatusBorder: tileViewColors.partsStatusBorder.light,
      listViewHeaderText: listViewColors.headerText.light,
      buttonText: listViewColors.buttonText.light,
      listViewHeaderBG: listViewColors.headerBG.light,
      listFooterTextColor: listViewColors.footerTextColor.light,
      listFooterCountText: listViewColors.footerCountText.light,
      listPaginationBorder: listPaginationColors.border.light,
      listPaginationEnable: listPaginationColors.enable.light,
      listPaginationDisable: listPaginationColors.disable.light,
      listPaginationButtonText: listPaginationColors.buttonText.light,
      lisViewHeaderBorder: listPaginationColors.border.light,
      listViewShiftBtnBorder: listViewColors.actionBorder.light,
      listViewShiftBtnText: listViewColors.actionText.light,

      workOrderStatausCalenderBG: workOrderColors.statausCalenderBG.light,
      calenderTextColor: calenderColors.textColor.light,
      calenderDisabledText: calenderColors.disabledText.light,

      shiftStatusGreetColor: shiftStatusColors.greetColor.light,
      shiftStatusTitleColor: shiftStatusColors.titleColor.light,
      dotIconColor: shiftStatusColors.dotIconColor.light,
      shiftStatusSubTitleColor: shiftStatusColors.subTitleColor.light,
      role: shiftStatusColors.role.light,
      bay: shiftStatusColors.bay.light,
      shiftStatusIconBGColor: shiftStatusColors.iconBGColor.light,
      shiftStatusIconColor: shiftStatusColors.iconColor.light,
      shiftTimeBG: shiftStatusColors.shiftTimeBG.light,
      shiftTimeBorder: shiftStatusColors.shiftTimeBorder.light,
      shiftTimeTitle: shiftStatusColors.shiftTimeTitle.light,
      shiftTimeSubTitle: shiftStatusColors.shiftTimeSubTitle.light,
      shiftLine: shiftStatusColors.shiftLine.light,
      shiftStatusActiveBG: shiftStatusColors.activeStatusBG.light,
      shiftStatusActiveText: shiftStatusColors.activeStatusText.light,

      beginShiftBorderColor: beginShiftColors.borderColor.light,
      beginShiftBGColor: beginShiftColors.BGColor.light,
      beginShiftTextColor: beginShiftColors.textColor.light,
      beginShiftIconColor: beginShiftColors.iconColor.light,
      beginShiftStatusColor: beginShiftColors.dotColor.light,

      endShiftBorderColor: endShiftColors.borderColor.light,
      endShiftBGColor: endShiftColors.BGColor.light,
      endShiftTextColor: endShiftColors.textColor.light,
      endShiftIconColor: endShiftColors.iconColor.light,
      endShiftStatusColor: endShiftColors.dotColor.light,

      workOrderIdBGColor: workOrderColors.idBGColor.light,
      workOrderIdTextColor: workOrderColors.idTextColor.light,
      workOrderIdValueColor: workOrderColors.idValueColor.light,
      repairIdTextColor: detailColors.repairIdTextColor.light,
      repairIdValueColor: detailColors.repairIdValueColor.light,
      partsDescTextColor: detailColors.partsDescTextColor.light,
      truckTitle: detailColors.truckTitle.light,
      descriptionTextColor: detailColors.descriptionTextColor.light,
      notificationIconColor: workOrderColors.notificationIconColor.light,
      notificationBGColor: workOrderColors.notificationBGColor.light,
      notificationCountBGColor: workOrderColors.notificationCountBGColor.light,
      notificationCountTextColor:
        workOrderColors.notificationCountTextColor.light,
      repairNumber: workOrderColors.repairNumber.light,

      lowPriorityBGColor: priorityColors.lowBG.light,
      leastPriorityBGColor: priorityColors.leastPriorityBGColor.light,
      lowPriorityTextColor: priorityColors.lowColor.light,
      lowPriorityBorderColor: priorityColors.lowBorder.light,
      leastPriorityBorderColor: priorityColors.lowColor.light,
      mediumPriorityBGColor: priorityColors.mediumBG.light,
      mediumPriorityTextColor: priorityColors.mediumColor.light,
      mediumPriorityBorderColor: priorityColors.mediumBorder.light,
      highPriorityBGColor: priorityColors.highBG.light,
      highPriorityTextColor: priorityColors.highColor.light,
      highPriorityBorderColor: priorityColors.highBorder.light,
      defaultStatusColor: priorityColors.defaultStatusColor.light,

      activeBG: statusColors.activeBG.light,
      activeColor: statusColors.activeColor.light,
      activeColorBorder: statusColors.activeColorBorder.light,
      completedBG: statusColors.completedBG.light,
      completedColor: statusColors.completedColor.light,
      completedColorBorder: statusColors.completedColorBorder.light,
      assignedBG: statusColors.assignedBG.light,
      assignedColor: statusColors.assignedColor.light,
      assignedColorBorder: statusColors.assignedColorBorder.light,

      separatorColor: detailColors.separator.light,
      cardSeparatorColor: detailColors.cardSeparator.light,
      detailLabelTextColor: detailColors.labelTextColor.light,
      detailValueTextColor: detailColors.valueTextColor.light,
      viewDetailsBorderColor: detailColors.borderColor.light,
      ctaBGColor: detailColors.ctaBGColor.light,
      viewDetailsTextColor: detailColors.textColor.light,
      ctaColor: detailColors.ctaColor.light,
      viewDetailsDotColor: detailColors.dotColor.light,

      viewAllCTABorder: viewAllColors.CTABorder.light,
      viewAllCTAIconColor: viewAllColors.CTAIcon.light,
      primaryIconColor: repairDetailsColors.primaryIconColor.light,
      messageIconColor: repairDetailsColors.messageIconColor.light,
      viewAllCTABG: viewAllColors.CTABG.light,
      viewAllTextColor: viewAllColors.textColor.light,
      progressTitle: repairDetailsColors.progressTitle.light,
      progressSubTitle: repairDetailsColors.progressBarCardSubTitle.light,
      progressSubTitleValue:
        repairDetailsColors.progressBarCardTitleValue.light,
      taskFlowInactive: repairDetailsColors.taskFlowInactive.light,
      taskFlowActive: repairDetailsColors.taskFlowActive.light,

      endShiftInfoBG: repairDetailsColors.endShiftInfoBG.light,
      endShiftInfoBorder: repairDetailsColors.endShiftInfoBorder.light,
      endShiftInfoTitle: repairDetailsColors.endShiftInfoTitle.light,
      endShiftInfoSubTitle: repairDetailsColors.endShiftInfoSubTitle.light,
      shiftSummaryTile: repairDetailsColors.shiftSummaryTile.light,
      shiftSummaryBorder: repairDetailsColors.shiftSummaryBorder.light,
      shiftSummaryKey: repairDetailsColors.shiftSummaryKey.light,
      shiftSummaryValue: repairDetailsColors.shiftSummaryValue.light,
      closeModalIconColor: repairDetailsColors.closeModalIconColor.light,
      notesModalBorder: repairDetailsColors.notesModalBorder.light,
      noteHeaderSeparator: repairDetailsColors.noteHeaderSeparator.light,
      notedModalBG: repairDetailsColors.notedModalBG.light,
      notedModalMainTitle: repairDetailsColors.notedModalMainTitle.light,
      saveCTABGColor: repairDetailsColors.saveCTABGColor.light,
      saveCTATitleColor: repairDetailsColors.saveCTATitleColor.light,
      saveCTAIconColor: repairDetailsColors.saveCTAIconColor.light,
      cancelCTABorderColor: repairDetailsColors.cancelCTABorderColor.light,
      cancelCTATextColor: repairDetailsColors.cancelCTATextColor.light,
      inCompletedRepairs: statusColors.closedColorBG.light,
      inCompletedRepairsText: statusColors.closedColorText.light,
      partsAssignedDisabledBG: workOrderColors.partsAssignedDisabledBG.light,
      partsAssignedDisabledBorder:
        workOrderColors.partsAssignedDisabledBorder.light,
      partsAssignedDisabledIcon:
        workOrderColors.partsAssignedDisabledIcon.light,
      partsAssigneEnabledIcon: workOrderColors.partsAssigneEnabledIcon.light,
      partsAssigneEnabledBorder:
        workOrderColors.partsAssigneEnabledBorder.light,
      partsAssignedEnabledBG: workOrderColors.partsAssignedEnabledBG.light,

      partsRequestedBGColor: partsStatusColors.requestedBG.light,
      partsRequestedBorderColor: partsStatusColors.requestedBorder.light,
      partsRequestedTextColor: partsStatusColors.requestedText.light,
      partsReservedBGColor: partsStatusColors.reservedBG.light,
      partsReservedBorderColor: partsStatusColors.reservedBorder.light,
      meterRangeInfoBG: repairDetailsColors.meterRangeInfoBG.light,
      meterRangeInfoBorder: repairDetailsColors.meterRangeInfoBorder.light,
      meterRangeInfoText: repairDetailsColors.meterRangeInfoText.light,
      updateMeterCTAText: repairDetailsColors.updateMeterCTAText.light,
      partsReservedTextColor: partsStatusColors.reservedText.light,
      partsOnOrderBGColor: partsStatusColors.onOrderBG.light,
      partsOnOrderBorderColor: partsStatusColors.onOrderBorder.light,
      partsOnOrderTextColor: partsStatusColors.onOrderText.light,
      partsIssuedBGColor: partsStatusColors.issuedBG.light,
      partsIssuedBorderColor: partsStatusColors.issuedBorder.light,
      partsIssuedTextColor: partsStatusColors.issuedText.light,
      errorText: createFlowColors.errorText.light,
      errorBG: createFlowColors.errorBG.light,
      disabledOverlayBG: dashboardColors.disabledOverlay.light,
      activityIndicatorColor: dashboardColors.activityIndicator.light,
      repairShiftTime: repairDetailsColors.repairShiftTime.light,
      workOrderSortBackground: dropdownColors.BGColor.light,
      workOrderSortSelected: workOrderColors.sortSelectedText.light,
      shopHomeWorkOrderSelectedTextColor:
        shopHomeColors.workOrderSelectedTextColor.light,
      borderColor: shopHomeColors.filterBorderColor.light,
      viewbackground: shopHomeColors.filterBackground.light,
      bGDisableColor: buttonColors.ctaDisableBG.light,
      textDisableColor: buttonColors.ctaDisableText.light,
      disabledText: repairDetailsColors.disabledText.light,
      enabledText: repairDetailsColors.enabledText.light,
      subTitleBadgeBg: buttonColors.subTitleBadgeBg.light,
      sortIconColor: commonColors.sortIcon.light,
      selectedSortIconColor: commonColors.sortSelectedIcon.light,
    },
    dark: {
      background: commonColors.lightBlack.dark,
      text: commonColors.white.dark,
      card: 'transparent',
      subText: commonColors.subTextDark.dark,
      priceTag: commonColors.red.light,
      priceTagTextColor: commonColors.white.dark,
      scoreBoardIconColor: scoreBoardColors.icon.dark,
      tileCardCTASelectedBorder:
        repairDetailsColors.tileCardCTASelectedBorder.dark,

      tabIconColor: dashboardColors.tabIconColor.dark,
      tabTextColor: dashboardColors.tabTextColor.dark,
      selectedCountTextColor: dashboardColors.selectedCountTextColor.dark,
      countTextColor: dashboardColors.countTextColor.dark,
      tabIconSelectedColor: dashboardColors.tabIconSelectedColor.dark,
      tabTextSelectedColor: dashboardColors.tabTextSelectedColor.dark,
      countBG: dashboardColors.countBG.dark,
      selectedCountBG: dashboardColors.selecetdCountBG.dark,

      shiftBGColor: dashboardColors.shiftCardBG.dark,
      workOrderCardBG: scoreBoardColors.cardBG.dark,
      workOrderCardBorder: commonColors.viewBorder.dark,
      workOrderTitle: commonColors.white.dark,
      workOrderSortIconsBG: workOrderColors.sortBG.dark,
      workOrderSortIconsBorder: workOrderColors.sortBorder.dark,
      workOrderTileViewIcon: workOrderColors.tileViewIcon.dark,
      workOrderListViewIconColor: workOrderColors.listViewIconColor.dark,
      workOrderTileViewSelectedIcon: workOrderColors.tileViewSelectedIcon.dark,
      workOrderListViewSelectedIconColor:
        workOrderColors.listViewSelectedIconColor.dark,
      workOrderFilterBorder: workOrderColors.filterBorder.dark,
      workOrderFilterSelectedText: workOrderColors.filterSelectedText.dark,
      workOrderFilterSelectedTextBorder:
        workOrderColors.filterSelectedTextBorder.dark,
      workOrderStatusSelectedTextBG:
        workOrderColors.workOrderStatusSelectedTextBG.dark,
      workOrderStatusSelectedText:
        workOrderColors.workOrderStatusSelectedText.dark,
      workOrderStatusSelectedTextBorder:
        workOrderColors.workOrderStatusSelectedTextBorder.dark,
      workOrderStatusTextBorder: workOrderColors.workOrderStatusTextBorder.dark,
      workOrderStatusTextBG: workOrderColors.workOrderStatusTextBG.dark,
      workOrderStatusText: workOrderColors.workOrderStatusText.dark,
      workOrderFilterText: workOrderColors.filterText.dark,
      workOrderViewAllCTAText: workOrderColors.viewAllCTAText.dark,
      workOrderCardListBorder: workOrderColors.cardBorder.dark,
      workOrderCardListSubTitle: workOrderColors.cardListSubTitle.dark,
      workOrderCardIconBG: workOrderColors.cardIconBG.dark,
      workOrderCardListTitle: workOrderColors.cardListTitle.dark,

      workOrderStatusBG: workOrderColors.statusBG.dark,
      workOrderStatausBorder: workOrderColors.statausBorder.dark,
      workOrderStatusTitle: workOrderColors.statusTitle.dark,
      workOrderStatusDate: workOrderColors.statusDate.dark,
      workOrderStatusDateTitle: workOrderColors.statusDateTitle.dark,
      workOrderStatusDateBG: workOrderColors.statusDateBG.dark,
      workOrderStatusDateBorder: workOrderColors.statusDateBorder.dark,
      workOrderStatusDateIcon: workOrderColors.statusDateIcon.dark,

      statusChartStatusesText: statusChartColors.statusesText.dark,
      statusChartStatusesBorder: statusChartColors.statusesBorder.dark,
      statusChartStatusesTotalText: statusChartColors.statusesTotalText.dark,
      statusChartStatusesSubTotalText:
        statusChartColors.statusesSubTotalText.dark,
      statusInprogress: statusChartColors.inprogress.dark,
      statusActive: statusChartColors.active.dark,
      statusAwaitingParts: statusChartColors.awaitingParts.dark,
      statusCompleted: statusChartColors.completed.dark,
      statusPieChartOuterBorder: statusPieChartColors.outerBorder.dark,
      statusPieChartBorderShadow: statusPieChartColors.borderShadow.dark,
      statusPieChartBorder: statusPieChartColors.border.dark,
      statusPieChartToolTipBG: statusPieChartColors.toolTipBG.dark,
      statusPieChartToolTipText: statusPieChartColors.toolTipText.dark,

      leaderBoardBG: leaderBoardColors.BG.dark,
      leaderBoardTitle: leaderBoardColors.title.dark,
      leaderBoardBorder: leaderBoardColors.border.dark,
      leaderBoardSubTitle: leaderBoardColors.subTitle.dark,
      leaderBoardCard: leaderBoardColors.cardBG.dark,
      leaderBoardCardBorder: leaderBoardColors.cardBorder.dark,
      leaderBoardCardLeaderName: leaderBoardColors.cardLeaderName.dark,
      leaderBoardCardSelfLeaderName: leaderBoardColors.cardSelfLeaderName.dark,
      leaderBoardCardSelfLeaderBG: leaderBoardColors.cardSelfLeaderBG.dark,
      leaderBoardCardLeaderRepairs: leaderBoardColors.cardLeaderRepairs.dark,
      leaderBoardSelectedCard: leaderBoardColors.selectedCard.dark,
      leaderBoardSelectedCardBorder: leaderBoardColors.selectedCardBorder.dark,
      leaderBoardRankTextColor: leaderBoardColors.rankTextColor.dark,
      leaderBoardBottomLine: leaderBoardColors.bottomLine.dark,
      leaderBoardBottomText: leaderBoardColors.bottomText.dark,

      repairBoardCardBorder: repairBoardColors.cardBorder.dark,

      LeaderOneColorBG: leaderRankColors.oneColorBG.dark,
      LeaderOneColorBorder: leaderRankColors.oneColorBorder.dark,
      LeaderTwoColorBG: leaderRankColors.twoColorBG.dark,
      LeaderTwoColorBorder: leaderRankColors.twoColorBorder.dark,
      LeaderThreeColorBG: leaderRankColors.threeColorBG.dark,
      LeaderThreeColorBorder: leaderRankColors.threeColorBorder.dark,
      LeaderRemainingColorBG: leaderRankColors.remainingColorBG.dark,
      LeaderRemainingColorBorder: leaderRankColors.remainingColorBorder.dark,
      LeaderRemainingColorText: leaderRankColors.remainingColorText.dark,
      LeaderFourIconBG: leaderRankColors.fourIconBG.dark,
      LeaderFourIconBorder: leaderRankColors.fourIconBorder.dark,

      listViewBG: listViewColors.BG.dark,
      listViewBorder: listViewColors.border.dark,
      tileViewBorder: tileViewColors.cardBorder.dark,
      tileViewSelectedBorder: tileViewColors.cardSelectedBorder.dark,
      tileViewSelectedShadow: tileViewColors.cardSelectedShadow.dark,
      tileViewSelectedButtonBorder: tileViewColors.cardSelectedButtonBorder.dark,
      partsViewBorder: tileViewColors.partsBorder.dark,
      partsStatusBorder: tileViewColors.partsStatusBorder.dark,
      listViewHeaderText: listViewColors.headerText.dark,
      buttonText: listViewColors.buttonText.dark,
      lisViewHeaderBG: commonColors.transparent.dark,
      listFooterTextColor: listViewColors.footerTextColor.dark,
      listFooterCountText: listViewColors.footerCountText.dark,
      listPaginationBorder: listPaginationColors.border.dark,
      listPaginationEnable: listPaginationColors.enable.dark,
      listPaginationDisable: listPaginationColors.disable.dark,
      listPaginationButtonText: listPaginationColors.buttonText.dark,
      lisViewHeaderBorder: listPaginationColors.border.dark,
      listViewHeaderBG: listViewColors.headerBG.dark,
      listViewShiftBtnBorder: listViewColors.actionBorder.dark,
      listViewShiftBtnText: listViewColors.actionText.dark,

      workOrderStatausCalenderBG: workOrderColors.statausCalenderBG.dark,
      calenderTextColor: calenderColors.textColor.dark,
      calenderDisabledText: calenderColors.disabledText.dark,

      shiftStatusGreetColor: shiftStatusColors.greetColor.dark,
      shiftStatusTitleColor: shiftStatusColors.titleColor.dark,
      dotIconColor: shiftStatusColors.dotIconColor.dark,
      shiftStatusSubTitleColor: shiftStatusColors.subTitleColor.dark,
      role: shiftStatusColors.role.dark,
      bay: shiftStatusColors.bay.dark,
      shiftStatusIconBGColor: shiftStatusColors.iconBGColor.dark,
      shiftStatusIconColor: shiftStatusColors.iconColor.dark,
      shiftTimeBG: shiftStatusColors.shiftTimeBG.dark,
      shiftTimeBorder: shiftStatusColors.shiftTimeBorder.dark,
      shiftTimeTitle: shiftStatusColors.shiftTimeTitle.dark,
      shiftTimeSubTitle: shiftStatusColors.shiftTimeSubTitle.dark,
      shiftLine: shiftStatusColors.shiftLine.dark,
      shiftStatusActiveBG: shiftStatusColors.activeStatusBG.dark,
      shiftStatusActiveText: shiftStatusColors.activeStatusText.dark,

      beginShiftBorderColor: beginShiftColors.borderColor.dark,
      beginShiftBGColor: beginShiftColors.BGColor.dark,
      beginShiftTextColor: beginShiftColors.textColor.dark,
      beginShiftIconColor: beginShiftColors.iconColor.dark,
      beginShiftStatusColor: beginShiftColors.dotColor.dark,

      endShiftBorderColor: endShiftColors.borderColor.dark,
      endShiftBGColor: endShiftColors.BGColor.dark,
      endShiftTextColor: endShiftColors.textColor.dark,
      endShiftIconColor: endShiftColors.iconColor.dark,
      endShiftStatusColor: endShiftColors.dotColor.dark,

      workOrderIdBGColor: workOrderColors.idBGColor.dark,
      workOrderIdTextColor: workOrderColors.idTextColor.dark,
      workOrderIdValueColor: workOrderColors.idValueColor.dark,
      repairIdTextColor: detailColors.repairIdTextColor.dark,
      repairIdValueColor: detailColors.repairIdValueColor.dark,
      partsDescTextColor: detailColors.partsDescTextColor.dark,
      truckTitle: detailColors.truckTitle.dark,
      descriptionTextColor: detailColors.descriptionTextColor.dark,
      notificationIconColor: workOrderColors.notificationIconColor.dark,
      notificationBGColor: workOrderColors.notificationBGColor.dark,
      notificationCountBGColor: workOrderColors.notificationCountBGColor.dark,
      notificationCountTextColor:
        workOrderColors.notificationCountTextColor.dark,
      repairNumber: workOrderColors.repairNumber.dark,

      lowPriorityBGColor: priorityColors.lowBG.dark,
      lowPriorityTextColor: priorityColors.lowColor.dark,
      lowPriorityBorderColor: priorityColors.lowBorder.dark,
      mediumPriorityBGColor: priorityColors.mediumBG.dark,
      mediumPriorityTextColor: priorityColors.mediumColor.dark,
      mediumPriorityBorderColor: priorityColors.mediumBorder.dark,
      highPriorityBGColor: priorityColors.highBG.dark,
      highPriorityTextColor: priorityColors.highColor.dark,
      highPriorityBorderColor: priorityColors.highBorder.dark,
      defaultStatusColor: priorityColors.defaultStatusColor.dark,

      activeBG: statusColors.activeBG.dark,
      activeColor: statusColors.activeColor.dark,
      activeColorBorder: statusColors.activeColorBorder.dark,
      completedBG: statusColors.completedBG.dark,
      completedColor: statusColors.completedColor.dark,
      completedColorBorder: statusColors.completedColorBorder.dark,
      assignedBG: statusColors.assignedBG.dark,
      assignedColor: statusColors.assignedColor.dark,
      assignedColorBorder: statusColors.assignedColorBorder.dark,

      separatorColor: detailColors.separator.dark,
      cardSeparatorColor: detailColors.cardSeparator.dark,
      detailLabelTextColor: detailColors.labelTextColor.dark,
      detailValueTextColor: detailColors.valueTextColor.dark,
      viewDetailsBorderColor: detailColors.borderColor.dark,
      ctaBGColor: detailColors.ctaBGColor.dark,
      viewDetailsTextColor: detailColors.textColor.dark,
      ctaColor: detailColors.ctaColor.dark,
      viewDetailsDotColor: detailColors.dotColor.dark,

      viewAllCTABorder: viewAllColors.CTABorder.dark,
      viewAllCTAIconColor: viewAllColors.CTAIcon.dark,
      primaryIconColor: repairDetailsColors.primaryIconColor.dark,
      messageIconColor: repairDetailsColors.messageIconColor.dark,
      viewAllCTABG: viewAllColors.CTABG.dark,
      viewAllTextColor: viewAllColors.textColor.dark,
      progressTitle: repairDetailsColors.progressTitle.dark,
      progressSubTitle: repairDetailsColors.progressBarCardSubTitle.dark,
      progressSubTitleValue: repairDetailsColors.progressBarCardTitleValue.dark,
      taskFlowInactive: repairDetailsColors.taskFlowInactive.dark,
      taskFlowActive: repairDetailsColors.taskFlowActive.dark,

      endShiftInfoBG: repairDetailsColors.endShiftInfoBG.dark,
      endShiftInfoBorder: repairDetailsColors.endShiftInfoBorder.dark,
      endShiftInfoTitle: repairDetailsColors.endShiftInfoTitle.dark,
      endShiftInfoSubTitle: repairDetailsColors.endShiftInfoSubTitle.dark,
      shiftSummaryTile: repairDetailsColors.shiftSummaryTile.dark,
      shiftSummaryBorder: repairDetailsColors.shiftSummaryBorder.dark,
      shiftSummaryKey: repairDetailsColors.shiftSummaryKey.dark,
      shiftSummaryValue: repairDetailsColors.shiftSummaryValue.dark,
      closeModalIconColor: repairDetailsColors.closeModalIconColor.dark,
      notesModalBorder: repairDetailsColors.notesModalBorder.dark,
      noteHeaderSeparator: repairDetailsColors.noteHeaderSeparator.dark,
      notedModalBG: repairDetailsColors.notedModalBG.dark,
      notedModalMainTitle: repairDetailsColors.notedModalMainTitle.dark,
      saveCTABGColor: repairDetailsColors.saveCTABGColor.dark,
      saveCTATitleColor: repairDetailsColors.saveCTATitleColor.dark,
      saveCTAIconColor: repairDetailsColors.saveCTAIconColor.dark,
      cancelCTABorderColor: repairDetailsColors.cancelCTABorderColor.dark,
      cancelCTATextColor: repairDetailsColors.cancelCTATextColor.dark,
      inCompletedRepairs: statusColors.closedColorBG.dark,
      inCompletedRepairsText: statusColors.closedColorText.dark,
      partsAssignedDisabledBG: workOrderColors.partsAssignedDisabledBG.dark,
      partsAssignedDisabledBorder:
        workOrderColors.partsAssignedDisabledBorder.dark,
      partsAssignedDisabledIcon: workOrderColors.partsAssignedDisabledIcon.dark,
      partsAssigneEnabledIcon: workOrderColors.partsAssigneEnabledIcon.dark,
      partsAssigneEnabledBorder: workOrderColors.partsAssigneEnabledBorder.dark,
      partsAssignedEnabledBG: workOrderColors.partsAssignedEnabledBG.dark,

      partsRequestedBGColor: partsStatusColors.requestedBG.dark,
      partsRequestedBorderColor: partsStatusColors.requestedBorder.dark,
      partsRequestedTextColor: partsStatusColors.requestedText.dark,
      partsReservedBGColor: partsStatusColors.reservedBG.dark,
      partsReservedBorderColor: partsStatusColors.reservedBorder.dark,
      meterRangeInfoBG: repairDetailsColors.meterRangeInfoBG.dark,
      meterRangeInfoBorder: repairDetailsColors.meterRangeInfoBorder.dark,
      meterRangeInfoText: repairDetailsColors.meterRangeInfoText.dark,
      updateMeterCTAText: repairDetailsColors.updateMeterCTAText.dark,
      partsReservedTextColor: partsStatusColors.reservedText.dark,
      partsOnOrderBGColor: partsStatusColors.onOrderBG.dark,
      partsOnOrderBorderColor: partsStatusColors.onOrderBorder.dark,
      partsOnOrderTextColor: partsStatusColors.onOrderText.dark,
      partsIssuedBGColor: partsStatusColors.issuedBG.dark,
      partsIssuedBorderColor: partsStatusColors.issuedBorder.dark,
      partsIssuedTextColor: partsStatusColors.issuedText.dark,
      errorText: createFlowColors.errorText.dark,
      errorBG: createFlowColors.errorBG.dark,
      disabledOverlayBG: dashboardColors.disabledOverlay.dark,
      activityIndicatorColor: dashboardColors.activityIndicator.dark,
      repairShiftTime: repairDetailsColors.repairShiftTime.dark,
      workOrdeViewBorder: shopHomeColors.workOrdeViewBorder.dark,
      scoreBoardIconBG: scoreBoardColors.iconBG.dark,
      leastPriorityBGColor: priorityColors.leastPriorityBGColor.dark,
      leastPriorityBorderColor: priorityColors.lowColor.dark,
      workOrderSortBackground: workOrderColors.sortBackground.dark,
      workOrderSortSelected: workOrderColors.sortSelectedText.dark,
      shopHomeWorkOrderSelectedTextColor:
        shopHomeColors.workOrderSelectedTextColor.dark,
      borderColor: shopHomeColors.filterBorderColor.dark,
      viewbackground: shopHomeColors.filterBackground.dark,
      bGDisableColor: buttonColors.ctaDisableBG.dark,
      textDisableColor: buttonColors.ctaDisableText.dark,
      disabledText: repairDetailsColors.disabledText.dark,
      enabledText: repairDetailsColors.enabledText.dark,
      subTitleBadgeBg: buttonColors.subTitleBadgeBg.dark,
      sortIconColor: commonColors.sortIcon.dark,
      selectedSortIconColor: commonColors.sortSelectedIcon.dark,
    },
  },

  OfflineMode: {
    light: {
      background: commonColors.white.light,
      text: commonColors.black.light,
      inputBg: commonColors.white.light,
    },
    dark: {
      background: commonColors.black.dark,
      text: commonColors.white.dark,
      inputBg: commonColors.black.dark,
    },
  },

  globalErrorModal: {
    light: {
      headerText: loginColors.headerText.light,
      headerSubText: forgotPasswordColors.subHeaderText.light,
      cancelBtnText: createFlowColors.cancelBtnText.light,
      cancelBtnBorder: createFlowColors.cancelBtnBorder.light,
      background: indirectActivityColors.modalOverlay.light,
      modalBG: repairDetailsColors.notedModalBG.light,
      lightBackground: indirectActivityColors.lightBackground.light,
    },
    dark: {
      headerText: loginColors.headerText.dark,
      headerSubText: forgotPasswordColors.subHeaderText.dark,
      cancelBtnText: createFlowColors.cancelBtnText.dark,
      cancelBtnBorder: createFlowColors.cancelBtnBorder.dark,
      background: indirectActivityColors.modalOverlay.dark,
      modalBG: repairDetailsColors.notedModalBG.dark,
      lightBackground: indirectActivityColors.lightBackground.dark,
    },
  },

  softUpdateBanner: {
    light: {
      headerText: loginColors.headerText.light,
      messageText: forgotPasswordColors.messageText.light,
      backgroundColor: ModalColors.background.light, 
      cancelBtnBorder: ModalColors.border.light,
      CTAText: buttonColors.CTAText.light,
      border: ModalColors.border.light
    },
    dark: {
      headerText: loginColors.headerText.dark,
      messageText: forgotPasswordColors.messageText.dark,
      backgroundColor: ModalColors.background.dark,
      cancelBtnBorder: ModalColors.border.dark,
      CTAText: buttonColors.CTAText.dark,
      border: ModalColors.border.dark
    },
  },

  ConfirmLogoutModal: {
    light: {
      headerText: loginColors.headerText.light,
      headerSubText: forgotPasswordColors.subHeaderText.light,
      closeModalIconColor: logout.closeIcon.light,
    },
    dark: {
      headerText: loginColors.headerText.dark,
      headerSubText: forgotPasswordColors.subHeaderText.dark,
      closeModalIconColor: logout.closeIcon.light,
    },
  },

  inputText: {
    light: {
      background: commonColors.white.light,
      text: commonColors.title.light,
      inputBg: commonColors.white.light,
      inputBorder: inputColors.border.light,
      inputDisabledBorder: inputColors.disabledBorder.light,
      inputBG: inputColors.BG.light,
      inputPlaceHolder: inputColors.placeholder.light,
      openEyeIcon: eyeColors.openIcon.light,
      closeEyeIcon: eyeColors.closeIcon.light,
      inputEditableBorder: inputColors.border.light,
      diabledBG: inputColors.diabledBG.light,
      diabledText: inputColors.diabledText.light,
      activeBorderColor: inputColors.activeBorder.light,
      hoverBorderColor: inputColors.hoverBorder.light,
    },
    dark: {
      background: commonColors.black.dark,
      text: commonColors.title.dark,
      inputBg: commonColors.black.dark,
      inputBorder: inputColors.border.dark,
      inputDisabledBorder: inputColors.disabledBorder.dark,
      inputBG: inputColors.BG.dark,
      inputPlaceHolder: inputColors.placeholder.dark,
      openEyeIcon: eyeColors.openIcon.dark,
      closeEyeIcon: eyeColors.closeIcon.dark,
      inputEditableBorder: inputColors.border.dark,
      diabledBG: inputColors.diabledBG.dark,
      diabledText: inputColors.diabledText.dark,
      activeBorderColor: inputColors.activeBorder.dark,
      hoverBorderColor: inputColors.hoverBorder.dark,
    },
  },

  dropdown: {
    light: {
      background: commonColors.white.light,
      text: commonColors.black.light,
      inputBg: commonColors.white.light,
      workOrderSortSelected: workOrderColors.sortSelectedText.light,
      workOrderDropdownPlaceholderText:
        workOrderColors.dropdownPlaceholderText.light,
      sortByText: workOrderColors.sortByText.light,
      workOrderSortIconColor: workOrderColors.icon.light,
      workOrderSortIconsBorder: dropdownColors.border.light,
      workOrderDisabledSortIconsBorder: dropdownColors.disabledBorder.light,
      workOrderSortBackground: dropdownColors.BGColor.light,
      mainBg: dropdownColors.mainBGColor.light,
      workOrderFilterDropdownTextColor:
        workOrderColors.filterDropdownTextColor.light,
      workOrderFilterDropdownSelectedTextBG:
        workOrderColors.filterDropdownSelectedTextBG.light,
      workOrderFilterDropdownSelectedTextColor:
        workOrderColors.filterDropdownSelectedTextColor.light,
      workOrderFilterDropdownMenuSelectedTextBG:
        workOrderColors.filterDropdownMenuSelectedTextBG.light,
      workOrderFilterDropdownMenuSelectedTextBorder:
        workOrderColors.filterDropdownMenuSelectedTextBorder.light,
      dropdownBGColor: dropdownColors.BGColor.light,
      selectedDropdownBGColor: dropdownColors.selectedBGColor.light,
      selectedDropdownTextColor: dropdownColors.selectedTextColor.light,
      dropdownTextColor: dropdownColors.textColor.light,
      selectedDropdownTickColor: dropdownColors.selectedTickColor.light,
      dropdownShadorColor: dropdownColors.shadorColor.light,
      selectedDropdownBorderColor: dropdownColors.selecetedBorderColor.light,
      dropdownTitleColor: dropdownColors.title.light,
      dropdownIcon: dropdownColors.icon.light,
      dropdownShadowColor: dropdownColors.shadowColor.light,
      formMandatory: createFlowColors.formMandatory.light,
      disabledBG: dropdownColors.disabledBG.light,
      searchBorder: dropdownColors.searchBorder.light,
      dropdownSearchBorder: dropdownColors.dropdownSearchBorder.light,
      selectedTickColor: dropdownColors.selectedTickColor.light,
      inputColor: inputColors.color.light,
      searchResultBGColor: searchInputColors.resultBGColor.light,
      borderColor: searchInputColors.border.light,
      openBorderColor: dropdownColors.openBorder.light,
      hoverBGColor: dropdownColors.hoverBGColor.light,
    },
    dark: {
      background: commonColors.black.dark,
      text: commonColors.white.dark,
      inputBg: commonColors.black.dark,
      workOrderSortSelected: workOrderColors.sortSelectedText.dark,
      workOrderDropdownPlaceholderText:
        workOrderColors.dropdownPlaceholderText.dark,
      sortByText: workOrderColors.sortByText.dark,
      workOrderSortIconColor: workOrderColors.icon.dark,
      workOrderSortIconsBorder: dropdownColors.border.dark,
      workOrderDisabledSortIconsBorder: dropdownColors.disabledBorder.dark,
      workOrderSortBackground: workOrderColors.sortBackground.dark,
      mainBg: dropdownColors.mainBGColor.dark,
      workOrderFilterDropdownTextColor:
        workOrderColors.filterDropdownTextColor.dark,
      workOrderFilterDropdownSelectedTextColor:
        workOrderColors.filterDropdownSelectedTextColor.dark,
      workOrderFilterDropdownMenuSelectedTextBG:
        workOrderColors.filterDropdownMenuSelectedTextBG.dark,
      workOrderFilterDropdownMenuSelectedTextBorder:
        workOrderColors.filterDropdownMenuSelectedTextBorder.dark,
      dropdownBGColor: dropdownColors.BGColor.dark,
      selectedDropdownBGColor: dropdownColors.selectedBGColor.dark,
      selectedDropdownTextColor: dropdownColors.selectedTextColor.dark,
      dropdownTextColor: dropdownColors.textColor.dark,
      selectedDropdownTickColor: dropdownColors.selectedTickColor.dark,
      dropdownShadorColor: dropdownColors.shadorColor.dark,
      selectedDropdownBorderColor: dropdownColors.selecetedBorderColor.dark,
      dropdownTitleColor: dropdownColors.title.dark,
      dropdownIcon: dropdownColors.icon.dark,
      dropdownShadowColor: dropdownColors.shadowColor.dark,
      formMandatory: createFlowColors.formMandatory.dark,
      disabledBG: dropdownColors.disabledBG.dark,
      searchBorder: dropdownColors.searchBorder.dark,
      dropdownSearchBorder: dropdownColors.dropdownSearchBorder.dark,
      selectedTickColor: dropdownColors.selectedTickColor.dark,
      inputColor: inputColors.color.dark,
      searchResultBGColor: searchInputColors.resultBGColor.dark,
      borderColor: searchInputColors.border.dark,
      openBorderColor: dropdownColors.openBorder.dark,
      hoverBGColor: dropdownColors.hoverBGColor.dark,
    },
  },

  WorkOrderScreen: {
    light: {},
    dark: {},
  },

  PartsScreen: {
    light: {},
    dark: {},
  },

  RepairDetailsScreen: {
    light: {
      background: commonColors.lightWhite.light,
      repairShiftSectionBGColor:
        repairDetailsColors.repairShiftSectionBGColor.light,
      repairShiftMainSectionBGColor:
        repairDetailsColors.repairShiftMainSectionBGColor.light,
      repairShiftTitle: repairDetailsColors.repairShiftTitle.light,
      repairShiftTitleText: repairDetailsColors.repairShiftTitleText.light,
      repairSiftSubTitle: repairDetailsColors.repairSiftSubTitle.light,
      repairTitle: repairDetailsColors.repairTitle.light,
      repairShiftTime: repairDetailsColors.repairShiftTime.light,
      repairSubShiftTime: repairDetailsColors.repairSubShiftTime.light,
      beginRepairIconColor: buttonColors.primaryText.light,
      newRepairCTABGColor: repairDetailsColors.newRepairCTABGColor.light,
      newRepairCTABorder: repairDetailsColors.newRepairCTABorder.light,
      newRepairCTAText: repairDetailsColors.newRepairCTAText.light,
      tabsBGColor: repairDetailsColors.tabsBGColor.light,
      tabsBorderColor: repairDetailsColors.tabsBorderColor.light,
      tabsTextColor: repairDetailsColors.tabsTextColor.light,
      tabsSelectedColor: repairDetailsColors.tabsSelectedColor.light,
      mainTabsSelectedColor: repairDetailsColors.mainTabsSelectedColor.light,
      mainTabDisabledColor: repairDetailsColors.mainTabDisabledColor.light,
      partsCountColor: repairDetailsColors.partsCountColor.light,
      tileViewBG: tileViewColors.BG.light,
      tileCardTitle: repairDetailsColors.tileCardTitle.light,
      tileCardStatus: repairDetailsColors.tileCardStatus.light,
      tileCardCTAText: repairDetailsColors.tileCardCTAText.light,
      tileCardConfirmText: repairDetailsColors.tileCardConfirmText.light,
      tileCardCTABorder: repairDetailsColors.tileCardCTABorder.light,
      tileCardCTABG: repairDetailsColors.tileCardCTABG.light,
      tileCardCTASelectedText:
        repairDetailsColors.tileCardCTASelectedText.light,
      titleCardCTASelectedBGColor:
        repairDetailsColors.titleCardCTASelectedBGColor.light,
      tileCardCTASelectedBorder:
        repairDetailsColors.tileCardCTASelectedBorder.light,
      tileCardNumberBorder: repairDetailsColors.tileCardNumberBorder.light,
      tileCardNumberText: repairDetailsColors.tileCardNumberText.light,
      titleCardActionColor: repairDetailsColors.titleCardActionColor.light,
      viewInstructionsCTAColor:
        repairDetailsColors.viewInstructionsCTAColor.light,
      noViewInstructionsCTAColor:
        repairDetailsColors.noViewInstructionsCTAColor.light,
      viewInstructionsBorderColor:
        repairDetailsColors.viewInstructionsBorderColor.light,
      lowPriorityBGColor: priorityColors.lowBG.light,
      lowPriorityTextColor: priorityColors.lowColor.light,
      lowPriorityBorderColor: priorityColors.lowBorder.light,
      mediumPriorityBGColor: priorityColors.mediumBG.light,
      mediumPriorityTextColor: priorityColors.mediumColor.light,
      mediumPriorityBorderColor: priorityColors.mediumBorder.light,
      highPriorityBGColor: priorityColors.highBG.light,
      highPriorityTextColor: priorityColors.highColor.light,
      highPriorityBorderColor: priorityColors.highBorder.light,
      defaultStatusColor: priorityColors.defaultStatusColor.light,
      repairDetailsTitle: repairDetailsColors.repairDetailsTitle.light,
      repairHistoryColor: repairDetailsColors.repairHistoryColor.light,
      workOrderStatusTitle: repairDetailsColors.workOrderStatusTitle.light,
      workOrderStatusRepairTitle:
        repairDetailsColors.workOrderStatusRepairTitle.light,
      workOrderStatusDesc: repairDetailsColors.workOrderStatusDesc.light,
      workOrderStatusCTABGColor:
        repairDetailsColors.workOrderStatusCTABGColor.light,
      workOrderStatusCTAText: repairDetailsColors.workOrderStatusCTAText.light,
      workOrderStatusEnabledCTABGColor:
        repairDetailsColors.workOrderStatusEnabledCTABGColor.light,
      workOrderStatusEnabledCTABorder:
        repairDetailsColors.workOrderStatusEnabledCTABorder.light,
      workOrderStatusEnabledCTAText:
        repairDetailsColors.workOrderStatusEnabledCTAText.light,
      repairDetailsId: repairDetailsColors.repairDetailsId.light,
      assetInfo: repairDetailsColors.assetInfo.light,
      assetInfoDetailsKey: repairDetailsColors.assetInfoDetailsKey.light,
      assetInfoDetailsValue: repairDetailsColors.assetInfoDetailsValue.light,
      progressTitle: repairDetailsColors.progressTitle.light,
      progressSubTitle: repairDetailsColors.progressSubTitle.light,
      progressSubTitleValue: repairDetailsColors.progressSubTitleValue.light,
      activeTime: repairDetailsColors.activeTime.light,
      activeTimeValue: repairDetailsColors.activeTimeValue.light,
      repairStatusCardBorder: repairDetailsColors.repairStatusCardBorder.light,
      separator: repairDetailsColors.separator.light,
      activeTimeBGColor: repairDetailsColors.activeTimeBGColor.light,
      activeTimeBorderColor: repairDetailsColors.activeTimeBorderColor.light,
      taskFlowInactive: repairDetailsColors.taskFlowInactive.light,
      taskFlowActive: repairDetailsColors.taskFlowActive.light,
      shiftLine: shiftStatusColors.repairShiftLine.light,
      completedRepairs: repairDetailsColors.completedRepairs.light,
      inCompletedRepairs: repairDetailsColors.inCompletedRepairs.light,
      completedRepairsText: repairDetailsColors.completedRepairsText.light,
      inCompletedRepairsText: repairDetailsColors.inCompletedRepairsText.light,
      disabledText: repairDetailsColors.disabledText.light,
      enabledText: repairDetailsColors.enabledText.light,

      searchTitle: repairDetailsColors.searchTitle.light,
      ownRequestCTABorder: repairDetailsColors.ownRequestCTABorder.light,
      ownRequestCTAText: repairDetailsColors.ownRequestCTAText.light,
      ownRequestCTAIconColor: repairDetailsColors.ownRequestCTAIconColor.light,
      searchBorder: repairDetailsColors.searchBorder.light,
      searchLeftIconColor: repairDetailsColors.searchLeftIconColor.light,
      searchRightIconColor: repairDetailsColors.searchRightIconColor.light,
      searchRightIconBGColor: repairDetailsColors.searchRightIconBGColor.light,
      searchRightIconBorderColor:
        repairDetailsColors.searchRightIconBorderColor.light,
      searchPlaceHodlerColor: repairDetailsColors.searchPlaceHodlerColor.light,
      scanIconBorderColor: repairDetailsColors.scanIconBorderColor.light,
      scanIconTextColor: repairDetailsColors.scanIconTextColor.light,
      scanIconBGColor: repairDetailsColors.scanIconBGColor.light,
      partsHistoryCTABorder: repairDetailsColors.partsHistoryCTABorder.light,
      partsHistoryCTAText: repairDetailsColors.partsHistoryCTAText.light,
      partsHistoryCTABG: repairDetailsColors.partsHistoryCTABG.light,
      partsHistoryCTAIconColor:
        repairDetailsColors.partsHistoryCTAIconColor.light,
      messagesCTABorder: repairDetailsColors.messagesCTABorder.light,
      messagesCTAText: repairDetailsColors.messagesCTAText.light,
      messagesCTAIconColor: repairDetailsColors.messagesCTAIconColor.light,
      messagesCountTextColor: repairDetailsColors.messagesCountTextColor.light,
      messagesCountBGColor: repairDetailsColors.messagesCountBGColor.light,
      configuredTitle: repairDetailsColors.configuredTitle.light,
      configuredSubTitle: repairDetailsColors.configuredSubTitle.light,
      submitPartsCTABorder: repairDetailsColors.submitPartsCTABorder.light,
      submitPartsCTAText: repairDetailsColors.submitPartsCTAText.light,
      submitPartsCTAIconColor:
        repairDetailsColors.submitPartsCTAIconColor.light,
      submitPartsCountTextColor:
        repairDetailsColors.submitPartsCountTextColor.light,
      submitPartsBGColor: repairDetailsColors.submitPartsBGColor.light,
      checkboxColor: repairDetailsColors.checkboxColor.light,
      checkboxSelectedColor: repairDetailsColors.checkboxSelectedColor.light,
      checkboxSelectedIconColor:
        repairDetailsColors.checkboxSelectedIconColor.light,
      qtyBorder: repairDetailsColors.qtyBorder.light,
      qtyMinusIconColor: repairDetailsColors.qtyMinusIconColor.light,
      qtyPlusIconColor: repairDetailsColors.qtyPlusIconColor.light,
      qtyTextColor: repairDetailsColors.qtyTextColor.light,
      qtyDotsColor: repairDetailsColors.qtyDotsColor.light,
      separatorColor: repairDetailsColors.separatorColor.light,
      listViewBorder: listViewColors.border.light,
      partBorder: listViewColors.lightBorder.light,
      listViewBG: listViewColors.BG.light,
      listViewHeaderText: listViewColors.configuredHeaderText.light,
      configuredListText: listViewColors.configuredListText.light,
      listViewHeaderBG: listViewColors.headerBG.light,
      listViewSelectedRowBG: listViewColors.selectedRowBGColor.light,
      listSelectedBorder: checkboxColors.selectedBorderColor.light,
      listUnSelectedBorder: checkboxColors.unSelectedBorderColor.light,
      listSelectedBG: checkboxColors.selectedBGColor.light,
      listUnSelectedBG: checkboxColors.unSelectedBGColor.light,

      saveCTABGColor: repairDetailsColors.saveCTABGColor.light,
      saveCTATitleColor: repairDetailsColors.saveCTATitleColor.light,
      saveCTAIconColor: repairDetailsColors.saveCTAIconColor.light,
      cancelCTABorderColor: repairDetailsColors.cancelCTABorderColor.light,
      cancelCTATextColor: repairDetailsColors.cancelCTATextColor.light,

      notesBorder: repairDetailsColors.notesBorder.light,
      notesFilterUnselectBGColor:
        repairDetailsColors.notesFilterUnselectBGColor.light,
      notesFilterSelectBGColor:
        repairDetailsColors.notesFilterSelectBGColor.light,
      notesFilterSelectBorder:
        repairDetailsColors.notesFilterSelectBorder.light,
      notesFilterText: repairDetailsColors.notesFilterText.light,
      notesTitleColor: repairDetailsColors.notesTitleColor.light,
      notesMainTitleColor: repairDetailsColors.notesMainTitleColor.light,
      notesTypeColor: repairDetailsColors.notesTypeColor.light,
      notesDescColor: repairDetailsColors.notesDescColor.light,
      notesUserColor: repairDetailsColors.notesUserColor.light,
      notesUserNameColor: repairDetailsColors.notesUserNameColor.light,
      notesUserBGColor: repairDetailsColors.notesUserBGColor.light,
      notesUserDate: repairDetailsColors.notesUserDate.light,
      notesUserDateIcon: repairDetailsColors.notesUserDateIcon.light,
      titleCardEditActionColor: repairDetailsColors.titleCardEditActionColor.light,
      viewFullNoteCTAColor: repairDetailsColors.viewFullNoteCTAColor.light,
      viewFullNoteIconColor: repairDetailsColors.viewFullNoteIconColor.light,
      divider: repairDetailsColors.divider.light,

      notesModalBorder: repairDetailsColors.notesModalBorder.light,
      noteHeaderSeparator: repairDetailsColors.noteHeaderSeparator.light,
      itemSeparator: repairDetailsColors.itemSeparator.light,
      bottomSeparator: repairDetailsColors.bottomSeparator.light,
      notedModalBG: repairDetailsColors.notedModalBG.light,
      notedModalMainTitle: repairDetailsColors.notedModalMainTitle.light,
      closeModalIconColor: repairDetailsColors.closeModalIconColor.light,
      notesModalTitle: repairDetailsColors.notesModalTitle.light,
      viewMessageTitle: repairDetailsColors.viewMessageTitle.light,
      changeModalStubTitle: repairDetailsColors.woStatusSubTitle.light,
      noteSectionBorder: repairDetailsColors.noteSectionBorder.light,
      noteSectionSelectedBorder:
        repairDetailsColors.noteSectionSelectedBorder.light,
      noteSectionSelectedBG: repairDetailsColors.noteSectionSelectedBG.light,
      noteSectionApplySelectedBG: repairDetailsColors.noteSectionSelectedBG.light,
      noteSectionTitle: repairDetailsColors.noteSectionTitle.light,
      noteSectionValue: repairDetailsColors.noteSectionValue.light,
      noteSectionRadioBorder: repairDetailsColors.noteSectionRadioBorder.light,
      noteSectionRadioSelectedBorder:
        repairDetailsColors.noteSectionRadioSelectedBorder.light,
      noteSectionRadioSelectedBG:
        repairDetailsColors.noteSectionRadioSelectedBG.light,
      noteSubjectTitle: repairDetailsColors.noteSubjectTitle.light,
      noteSubjectPlaceholder: repairDetailsColors.noteSubjectPlaceholder.light,
      charactersText: repairDetailsColors.charactersText.light,
      noteSummaryTitle: repairDetailsColors.noteSummaryTitle.light,
      noteSummaryPlaceholder: repairDetailsColors.noteSummaryPlaceholder.light,
      noteLog: repairDetailsColors.noteLog.light,
      noteLogLeftVal: repairDetailsColors.noteLogLeftVal.light,
      createdKey: repairDetailsColors.createdKey.light,
      createdValue: repairDetailsColors.createdValue.light,
      cancelCTABorder: repairDetailsColors.cancelCTABorder.light,
      changeSelectionBorder: repairDetailsColors.changeSelectionBorder.light,
      cancelCTAText: repairDetailsColors.cancelCTAText.light,
      saveNoteCTAEnabledBG: repairDetailsColors.saveNoteCTAEnabledBG.light,
      saveNoteCTAEnabledBGText:
        repairDetailsColors.saveNoteCTAEnabledBGText.light,
      saveNoteCTADisabledBG: repairDetailsColors.saveNoteCTADisabledBG.light,
      saveNoteCTADisabledText:
        repairDetailsColors.saveNoteCTADisabledText.light,
      listeningEnabledCTABG: repairDetailsColors.listeningEnabledCTABG.light,
      listeningEnabledCTAText:
        repairDetailsColors.listeningEnabledCTAText.light,
      listeningEnableIconColor:
        repairDetailsColors.listeningEnableIconColor.light,
      listeningEnableIconBG: repairDetailsColors.listeningEnableIconBG.light,
      listeningDisableIconColor:
        repairDetailsColors.listeningDisableIconColor.light,
      listeningDisableIconBG: repairDetailsColors.listeningDisableIconBG.light,
      mandatoryIconColor: repairDetailsColors.mandatoryIconColor.light,
      mandatoryIconBG: repairDetailsColors.mandatoryIconBG.light,
      woStatusTitle: repairDetailsColors.woStatusTitle.light,
      woStatusDesc: repairDetailsColors.woStatusDesc.light,
      selectedtabBGColor: repairDetailsColors.selectedtabBGColor.light,
      selectedtabBorder: repairDetailsColors.selectedtabBorder.light,
      indirectActivityModalOverlay: indirectActivityColors.modalOverlay.light,
      indirectActivityConfirmationText:
        indirectActivityColors.selectedActivityTitle.light,
      indirectActivitySelectedActivityBorder:
        indirectActivityColors.selectedActivityBorder.light,
      indirectActivitySelectedActivityBackground:
        indirectActivityColors.selectedActivityBackground.light,
      indirectActivitySelectedActivityText:
        indirectActivityColors.selectedActivityText.light,

      partsSearchResultTitle: repairDetailsColors.partsSearchResultTitle.light,
      partsFoundBG: repairDetailsColors.partsFoundBG.light,
      partsFoundBText: repairDetailsColors.partsFoundBText.light,
      partsResultTitleBorder: repairDetailsColors.partsResultTitleBorder.light,
      partsResultTitleColor: repairDetailsColors.partsResultTitleColor.light,
      partsResultStatus: repairDetailsColors.partsResultStatus.light,
      partsResultSubTitleColor:
        repairDetailsColors.partsResultSubTitleColor.light,
      partsResultsPartName: repairDetailsColors.partsResultsPartName.light,
      partsResultsPartDesc: repairDetailsColors.partsResultsPartDesc.light,
      partsResultDescColor: repairDetailsColors.partsResultDescColor.light,
      outOfStockColor: repairDetailsColors.outOfStockColor.light,
      outOfStockBG: repairDetailsColors.outOfStockBG.light,
      inStockColor: repairDetailsColors.inStockColor.light,
      inStockBG: repairDetailsColors.inStockBG.light,
      quantityDisableBorder: repairDetailsColors.quantityDisableBorder.light,
      quantityEnableBorder: repairDetailsColors.quantityEnableBorder.light,
      quantityDisableIconColor:
        repairDetailsColors.quantityDisableIconColor.light,
      quantityDisableTextColor:
        repairDetailsColors.quantityDisableTextColor.light,
      quantityEnableIconColor:
        repairDetailsColors.quantityEnableIconColor.light,
      quantityEnableTextColor:
        repairDetailsColors.quantityEnableTextColor.light,
      addDisabledBG: repairDetailsColors.addDisabledBG.light,
      addDisabledText: repairDetailsColors.addDisabledText.light,
      addEnabledBorder: repairDetailsColors.addEnabledBorder.light,
      addEnabledText: repairDetailsColors.addEnabledText.light,
      closeIconColor: repairDetailsColors.closeIconColor.light,
      noPartsResultBG: repairDetailsColors.noPartsResultBG.light,
      noPartsResultBorder: repairDetailsColors.noPartsResultBorder.light,
      noPartsResultIconBG: repairDetailsColors.noPartsResultIconBG.light,
      noPartsResultIconText: repairDetailsColors.noPartsResultIconText.light,
      noPartsResultTitle: repairDetailsColors.noPartsResultTitle.light,
      noPartsResultDesc: repairDetailsColors.noPartsResultDesc.light,

      confirmPartsSubTitle: repairDetailsColors.confirmPartsSubTitle.light,
      header: repairDetailsColors.header.light,
      partName: repairDetailsColors.partName.light,
      reqQuantity: repairDetailsColors.reqQuantity.light,
      partNumberKey: repairDetailsColors.partNumberKey.light,
      partNumberValue: repairDetailsColors.partNumberValue.light,
      partNumberValueBorder: repairDetailsColors.partNumberValueBorder.light,
      partIdKey: repairDetailsColors.partIdKey.light,
      partIdValueBorder: repairDetailsColors.partIdValueBorder.light,
      woDetails: repairDetailsColors.woDetails.light,
      bottomTabsKey: repairDetailsColors.bottomTabsKey.light,
      bottomTabsvalue: repairDetailsColors.bottomTabsvalue.light,
      bottomTabsBorder: repairDetailsColors.bottomTabsBorder.light,
      separatorLineParts: repairDetailsColors.separatorLineParts.light,
      adddQuantity: repairDetailsColors.adddQuantity.light,
      addPartsNoteBg: repairDetailsColors.addPartsNoteBg.light,
      viewNoteBG: repairDetailsColors.viewNoteBG.light,
      viewNoteInfoBorder: repairDetailsColors.viewNoteInfoBorder.light,
      addPartsNoteBorder: repairDetailsColors.addPartsNoteBorder.light,
      addPartsNoteText: repairDetailsColors.addPartsNoteText.light,
      workOrderStatusDateIcon: workOrderColors.statusDateIcon.light,
      inputBorder: inputColors.border.light,
      shopHomeWorkOrderSelectedTextColor:
        shopHomeColors.workOrderSelectedTextColor.light,
      leastPriorityBGColor: priorityColors.leastPriorityBGColor.light,
      leastPriorityBorderColor: priorityColors.lowColor.light,
      pendingBackground: repairDetailsColors.pendingBackground.light,
      issuedBackground: repairDetailsColors.issuedBackground.light,
      readyBackground: repairDetailsColors.readyBackground.light,
      approvedBackground: repairDetailsColors.approvedBackground.light,
      rejectedBackground: repairDetailsColors.rejectedBackground.light,
      requestedBackground: repairDetailsColors.requestedBackground.light,
      onOrderBackground: repairDetailsColors.onOrderBackground.light,
      pendingBorder: repairDetailsColors.pendingBorder.light,
      issuedBorder: repairDetailsColors.issuedBorder.light,
      readyBorder: repairDetailsColors.readyBorder.light,
      onOrderBorder: repairDetailsColors.onOrderBorder.light,
      defaultStatusBackground:
        repairDetailsColors.defaultStatusBackground.light,
      defaultStatusText: repairDetailsColors.defaultStatusText.light,
      defaultStatusBorder: repairDetailsColors.defaultStatusBorder.light,
      addPartsBtnBorder: listViewColors.actionBorder.light,
      addPartsBtnText: listViewColors.actionText.light,
      partsAssignedDisabledBG: workOrderColors.partsAssignedDisabledBG.light,
      partsAssignedDisabledBorder:
        workOrderColors.partsAssignedDisabledBorder.light,
      partsAssignedDisabledIcon:
        workOrderColors.partsAssignedDisabledIcon.light,
      partsAssigneEnabledIcon: workOrderColors.partsAssigneEnabledIcon.light,
      partsAssigneEnabledBorder:
        workOrderColors.partsAssigneEnabledBorder.light,
      partsAssignedEnabledBG: workOrderColors.partsAssignedEnabledBG.light,
      workOrderTileViewIcon: workOrderColors.tileViewIcon.light,
      workOrdeViewBorder: shopHomeColors.workOrdeViewBorder.light,
      workOrderListViewIconColor: workOrderColors.listViewIconColor.light,
      workOrderTileViewSelectedIcon: workOrderColors.tileViewSelectedIcon.light,
      workOrderListViewSelectedIconColor:
        workOrderColors.listViewSelectedIconColor.light,
      workOrderSortIconsBorder: workOrderColors.sortBorderAlt.light,
      tileViewBorder: tileViewColors.border.light,
      imageBorder: repairDetailsColors.imageColor.light,
      viewDetailsTextColor: detailColors.textColor.light,
      openEyeIcon: eyeColors.openIcon.light,
      detailLabelTextColor: detailColors.labelTextColor.light,

      partMessagesBGColor: repairDetailsColors.partMessagesBGColor.light,
      selectedCountBG: dashboardColors.selecetdCountBG.light,
      countBG: dashboardColors.countBG.light,
      unReadMesageBGColor: repairDetailsColors.unReadMesageBGColor.light,
      ownPartsBorder: repairDetailsColors.ownPartsBorder.light,
      additionBadgeBG: repairDetailsColors.additionBadgeBG.light,
      inputPlaceHolder: searchInputColors.placeholder.light,
      charCountBG: repairDetailsColors.charCountBG.light,
      inputDisabledBorder: inputColors.disabledBorder.light,
      inputEditableBorder: inputColors.border.light,
      partsReservedBGColor: partsStatusColors.reservedBG.light,
      partsReservedBorderColor: partsStatusColors.reservedBorder.light,
      meterRangeInfoBG: repairDetailsColors.meterRangeInfoBG.light,
      meterRangeInfoBorder: repairDetailsColors.meterRangeInfoBorder.light,
      meterRangeInfoText: repairDetailsColors.meterRangeInfoText.light,
      updateMeterCTAText: repairDetailsColors.updateMeterCTAText.light,
      partsViewDisabledBGColor:
        repairDetailsColors.partsViewDisabledBGColor.light,
      accumulatedHoursExceededColor:
        repairDetailsColors.accumulatedHoursExceededColor.light,
      meterModalTitleIconColor:
        repairDetailsColors.meterModalTitleIconColor.light,
      viewAllCTAIconColor: viewAllColors.CTAIcon.dark,
      errorText: createFlowColors.errorText.light,
      errorBG: createFlowColors.errorBG.light,
      notesTickMark: repairDetailsColors.notesTickMark.light,
      taskNameBG: repairDetailsColors.taskNameBG.light,
      accumulatedErrorText: repairDetailsColors.accumulatedErrorText.light,
      workOrderFilterSelectedTextBorder:
        workOrderColors.filterSelectedTextBorder.light,
      tabTextSelectedColor: helperColors.tabTextSelectedColor.light,
      repairHistoryNoteTitle: repairDetailsColors.repairHistoryNoteTitle.light,
      openBorderColor: dropdownColors.openBorder.light,
      selectedDropdownBGColor: dropdownColors.selectedBGColor.light,
      selectedDropdownTextColor: dropdownColors.selectedTextColor.light,
      dropdownBGColor: dropdownColors.BGColor.light,
      dropdownShadowColor: dropdownColors.shadowColor.light,
      /** Separator line between groups inside the grouped dropdown panel */
      groupedDropdownSeparatorColor: dropdownColors.selectedBGColor.light,
      dropdownIcon: dropdownColors.icon.light,
      bGDisableColor: buttonColors.ctaDisableBG.light,
      bgDisableTitle: buttonColors.badgeDisabledText.light,
      sortIconColor: commonColors.sortIcon.light,
      selectedSortIconColor: commonColors.sortSelectedIcon.light,
      repairHistoryplaceholderText: dropdownColors.textColor.light
    },
    dark: {
      background: commonColors.lightWhite.dark,
      repairShiftSectionBGColor:
        repairDetailsColors.repairShiftSectionBGColor.dark,
      repairShiftMainSectionBGColor:
        repairDetailsColors.repairShiftMainSectionBGColor.dark,
      repairShiftTitle: repairDetailsColors.repairShiftTitle.dark,
      repairShiftTitleText: repairDetailsColors.repairShiftTitleText.dark,
      repairSiftSubTitle: repairDetailsColors.repairSiftSubTitle.dark,
      repairTitle: repairDetailsColors.repairTitle.dark,
      repairShiftTime: repairDetailsColors.repairShiftTime.dark,
      repairSubShiftTime: repairDetailsColors.repairSubShiftTime.dark,
      beginRepairIconColor: buttonColors.primaryText.dark,
      newRepairCTABGColor: repairDetailsColors.newRepairCTABGColor.dark,
      newRepairCTABorder: repairDetailsColors.newRepairCTABorder.dark,
      newRepairCTAText: repairDetailsColors.newRepairCTAText.dark,
      tabsBGColor: repairDetailsColors.tabsBGColor.dark,
      tabsBorderColor: repairDetailsColors.tabsBorderColor.dark,
      tabsTextColor: repairDetailsColors.tabsTextColor.dark,
      tabsSelectedColor: repairDetailsColors.tabsSelectedColor.dark,
      mainTabsSelectedColor: repairDetailsColors.mainTabsSelectedColor.dark,
      mainTabDisabledColor: repairDetailsColors.mainTabDisabledColor.dark,
      partsCountColor: repairDetailsColors.partsCountColor.dark,
      tileViewBG: tileViewColors.BG.dark,
      tileCardTitle: repairDetailsColors.tileCardTitle.dark,
      tileCardStatus: repairDetailsColors.tileCardStatus.dark,
      tileCardCTAText: repairDetailsColors.tileCardCTAText.dark,
      tileCardConfirmText: repairDetailsColors.tileCardConfirmText.dark,
      tileCardCTABorder: repairDetailsColors.tileCardCTABorder.dark,
      tileCardCTABG: repairDetailsColors.tileCardCTABG.dark,
      tileCardCTASelectedText: repairDetailsColors.tileCardCTASelectedText.dark,
      titleCardCTASelectedBGColor:
        repairDetailsColors.titleCardCTASelectedBGColor.dark,
      tileCardCTASelectedBorder:
        repairDetailsColors.tileCardCTASelectedBorder.dark,
      tileCardNumberBorder: repairDetailsColors.tileCardNumberBorder.dark,
      tileCardNumberText: repairDetailsColors.tileCardNumberText.dark,
      titleCardActionColor: repairDetailsColors.titleCardActionColor.dark,
      viewInstructionsCTAColor:
        repairDetailsColors.viewInstructionsCTAColor.dark,
      noViewInstructionsCTAColor:
        repairDetailsColors.noViewInstructionsCTAColor.dark,
      lowPriorityBGColor: priorityColors.lowBG.dark,
      lowPriorityTextColor: priorityColors.lowColor.dark,
      lowPriorityBorderColor: priorityColors.lowBorder.dark,
      mediumPriorityBGColor: priorityColors.mediumBG.dark,
      mediumPriorityTextColor: priorityColors.mediumColor.dark,
      mediumPriorityBorderColor: priorityColors.mediumBorder.dark,
      highPriorityBGColor: priorityColors.highBG.dark,
      highPriorityTextColor: priorityColors.highColor.dark,
      highPriorityBorderColor: priorityColors.highBorder.dark,
      defaultStatusColor: priorityColors.defaultStatusColor.dark,
      repairDetailsTitle: repairDetailsColors.repairDetailsTitle.dark,
      repairHistoryColor: repairDetailsColors.repairHistoryColor.dark,
      workOrderStatusTitle: repairDetailsColors.workOrderStatusTitle.dark,
      workOrderStatusRepairTitle:
        repairDetailsColors.workOrderStatusRepairTitle.dark,
      workOrderStatusDesc: repairDetailsColors.workOrderStatusDesc.dark,
      workOrderStatusCTABGColor:
        repairDetailsColors.workOrderStatusCTABGColor.dark,
      workOrderStatusCTAText: repairDetailsColors.workOrderStatusCTAText.dark,
      workOrderStatusEnabledCTABGColor:
        repairDetailsColors.workOrderStatusEnabledCTABGColor.dark,
      workOrderStatusEnabledCTABorder:
        repairDetailsColors.workOrderStatusEnabledCTABorder.dark,
      workOrderStatusEnabledCTAText:
        repairDetailsColors.workOrderStatusEnabledCTAText.dark,
      repairDetailsId: repairDetailsColors.repairDetailsId.dark,
      assetInfo: repairDetailsColors.assetInfo.dark,
      assetInfoDetailsKey: repairDetailsColors.assetInfoDetailsKey.dark,
      assetInfoDetailsValue: repairDetailsColors.assetInfoDetailsValue.dark,
      progressTitle: repairDetailsColors.progressTitle.dark,
      progressSubTitle: repairDetailsColors.progressSubTitle.dark,
      progressSubTitleValue: repairDetailsColors.progressSubTitleValue.dark,
      activeTime: repairDetailsColors.activeTime.dark,
      activeTimeValue: repairDetailsColors.activeTimeValue.dark,
      repairStatusCardBorder: repairDetailsColors.repairStatusCardBorder.dark,
      separator: repairDetailsColors.separator.dark,
      activeTimeBGColor: repairDetailsColors.activeTimeBGColor.dark,
      activeTimeBorderColor: repairDetailsColors.activeTimeBorderColor.dark,
      taskFlowInactive: repairDetailsColors.taskFlowInactive.dark,
      taskFlowActive: repairDetailsColors.taskFlowActive.dark,
      shiftLine: shiftStatusColors.repairShiftLine.dark,
      completedRepairs: repairDetailsColors.completedRepairs.dark,
      inCompletedRepairs: repairDetailsColors.inCompletedRepairs.dark,
      completedRepairsText: repairDetailsColors.completedRepairsText.dark,
      inCompletedRepairsText: repairDetailsColors.inCompletedRepairsText.dark,
      disabledText: repairDetailsColors.disabledText.dark,
      enabledText: repairDetailsColors.enabledText.dark,

      searchTitle: repairDetailsColors.searchTitle.dark,
      ownRequestCTABorder: repairDetailsColors.ownRequestCTABorder.dark,
      ownRequestCTAText: repairDetailsColors.ownRequestCTAText.dark,
      ownRequestCTAIconColor: repairDetailsColors.ownRequestCTAIconColor.dark,
      searchBorder: repairDetailsColors.searchBorder.dark,
      searchLeftIconColor: repairDetailsColors.searchLeftIconColor.dark,
      searchRightIconColor: repairDetailsColors.searchRightIconColor.dark,
      searchRightIconBGColor: repairDetailsColors.searchRightIconBGColor.dark,
      searchRightIconBorderColor:
        repairDetailsColors.searchRightIconBorderColor.dark,
      searchPlaceHodlerColor: repairDetailsColors.searchPlaceHodlerColor.dark,
      scanIconBorderColor: repairDetailsColors.scanIconBorderColor.dark,
      scanIconTextColor: repairDetailsColors.scanIconTextColor.dark,
      scanIconBGColor: repairDetailsColors.scanIconBGColor.dark,
      partsHistoryCTABorder: repairDetailsColors.partsHistoryCTABorder.dark,
      partsHistoryCTAText: repairDetailsColors.partsHistoryCTAText.dark,
      partsHistoryCTABG: repairDetailsColors.partsHistoryCTABG.dark,
      partsHistoryCTAIconColor:
        repairDetailsColors.partsHistoryCTAIconColor.dark,
      messagesCTABorder: repairDetailsColors.messagesCTABorder.dark,
      messagesCTAText: repairDetailsColors.messagesCTAText.dark,
      messagesCTAIconColor: repairDetailsColors.messagesCTAIconColor.dark,
      messagesCountTextColor: repairDetailsColors.messagesCountTextColor.dark,
      messagesCountBGColor: repairDetailsColors.messagesCountBGColor.dark,
      configuredTitle: repairDetailsColors.configuredTitle.dark,
      configuredSubTitle: repairDetailsColors.configuredSubTitle.dark,
      submitPartsCTABorder: repairDetailsColors.submitPartsCTABorder.dark,
      submitPartsCTAText: repairDetailsColors.submitPartsCTAText.dark,
      submitPartsCTAIconColor: repairDetailsColors.submitPartsCTAIconColor.dark,
      submitPartsCountTextColor:
        repairDetailsColors.submitPartsCountTextColor.dark,
      submitPartsBGColor: repairDetailsColors.submitPartsBGColor.dark,
      checkboxColor: repairDetailsColors.checkboxColor.dark,
      checkboxSelectedColor: repairDetailsColors.checkboxSelectedColor.dark,
      checkboxSelectedIconColor:
        repairDetailsColors.checkboxSelectedIconColor.dark,
      qtyBorder: repairDetailsColors.qtyBorder.dark,
      qtyMinusIconColor: repairDetailsColors.qtyMinusIconColor.dark,
      qtyPlusIconColor: repairDetailsColors.qtyPlusIconColor.dark,
      qtyTextColor: repairDetailsColors.qtyTextColor.dark,
      qtyDotsColor: repairDetailsColors.qtyDotsColor.dark,
      separatorLineColor: repairDetailsColors.separatorColor.dark,
      listViewBorder: listViewColors.border.dark,
      partBorder: listViewColors.lightBorder.dark,
      listViewBG: listViewColors.BG.dark,
      separatorColor: detailColors.separator.dark,
      listViewHeaderText: listViewColors.configuredHeaderText.dark,
      configuredListText: listViewColors.configuredListText.dark,
      listViewHeaderBG: listViewColors.headerBG.dark,
      listViewSelectedRowBG: listViewColors.selectedRowBGColor.dark,
      listSelectedBorder: checkboxColors.selectedBorderColor.dark,
      listUnSelectedBorder: checkboxColors.unSelectedBorderColor.dark,
      listSelectedBG: checkboxColors.selectedBGColor.dark,
      listUnSelectedBG: checkboxColors.unSelectedBGColor.dark,

      saveCTABGColor: repairDetailsColors.saveCTABGColor.dark,
      saveCTATitleColor: repairDetailsColors.saveCTATitleColor.dark,
      saveCTAIconColor: repairDetailsColors.saveCTAIconColor.dark,
      cancelCTABorderColor: repairDetailsColors.cancelCTABorderColor.dark,
      cancelCTATextColor: repairDetailsColors.cancelCTATextColor.dark,

      notesBorder: repairDetailsColors.notesBorder.dark,
      notesFilterUnselectBGColor:
        repairDetailsColors.notesFilterUnselectBGColor.dark,
      notesFilterSelectBGColor:
        repairDetailsColors.notesFilterSelectBGColor.dark,
      notesFilterSelectBorder: repairDetailsColors.notesFilterSelectBorder.dark,
      notesFilterText: repairDetailsColors.notesFilterText.dark,
      notesTitleColor: repairDetailsColors.notesTitleColor.dark,
      notesMainTitleColor: repairDetailsColors.notesMainTitleColor.dark,
      notesTypeColor: repairDetailsColors.notesTypeColor.dark,
      notesDescColor: repairDetailsColors.notesDescColor.dark,
      notesUserColor: repairDetailsColors.notesUserColor.dark,
      notesUserNameColor: repairDetailsColors.notesUserNameColor.dark,
      notesUserBGColor: repairDetailsColors.notesUserBGColor.dark,
      notesUserDate: repairDetailsColors.notesUserDate.dark,
      notesUserDateIcon: repairDetailsColors.notesUserDateIcon.dark,
      titleCardEditActionColor: repairDetailsColors.titleCardEditActionColor.dark,
      viewFullNoteCTAColor: repairDetailsColors.viewFullNoteCTAColor.dark,
      viewFullNoteIconColor: repairDetailsColors.viewFullNoteIconColor.dark,
      divider: repairDetailsColors.divider.dark,

      notesModalBorder: repairDetailsColors.notesModalBorder.dark,
      noteHeaderSeparator: repairDetailsColors.noteHeaderSeparator.dark,
      bottomSeparator: repairDetailsColors.bottomSeparator.dark,
      itemSeparator: repairDetailsColors.itemSeparator.dark,
      notedModalBG: repairDetailsColors.notedModalBG.dark,
      notedModalMainTitle: repairDetailsColors.notedModalMainTitle.dark,
      closeModalIconColor: repairDetailsColors.closeModalIconColor.dark,
      notesModalTitle: repairDetailsColors.notesModalTitle.dark,
      viewMessageTitle: repairDetailsColors.viewMessageTitle.dark,
      changeModalStubTitle: repairDetailsColors.woStatusSubTitle.dark,
      noteSectionBorder: repairDetailsColors.noteSectionBorder.dark,
      noteSectionSelectedBorder:
        repairDetailsColors.noteSectionSelectedBorder.dark,
      noteSectionSelectedBG: repairDetailsColors.noteSectionSelectedBG.dark,
      noteSectionApplySelectedBG: repairDetailsColors.noteSectionSelectedBG.dark,
      noteSectionTitle: repairDetailsColors.noteSectionTitle.dark,
      noteSectionValue: repairDetailsColors.noteSectionValue.dark,
      noteSectionRadioBorder: repairDetailsColors.noteSectionRadioBorder.dark,
      noteSectionRadioSelectedBorder:
        repairDetailsColors.noteSectionRadioSelectedBorder.dark,
      noteSectionRadioSelectedBG:
        repairDetailsColors.noteSectionRadioSelectedBG.dark,
      noteSubjectTitle: repairDetailsColors.noteSubjectTitle.dark,
      noteSubjectPlaceholder: repairDetailsColors.noteSubjectPlaceholder.dark,
      charactersText: repairDetailsColors.charactersText.dark,
      noteSummaryTitle: repairDetailsColors.noteSummaryTitle.dark,
      noteSummaryPlaceholder: repairDetailsColors.noteSummaryPlaceholder.dark,
      noteLog: repairDetailsColors.noteLog.dark,
      noteLogLeftVal: repairDetailsColors.noteLogLeftVal.dark,
      createdKey: repairDetailsColors.createdKey.dark,
      createdValue: repairDetailsColors.createdValue.dark,
      cancelCTABorder: repairDetailsColors.cancelCTABorder.dark,
      changeSelectionBorder: repairDetailsColors.changeSelectionBorder.dark,
      cancelCTAText: repairDetailsColors.cancelCTAText.dark,
      saveNoteCTAEnabledBG: repairDetailsColors.saveNoteCTAEnabledBG.dark,
      saveNoteCTAEnabledBGText:
        repairDetailsColors.saveNoteCTAEnabledBGText.dark,
      saveNoteCTADisabledBG: repairDetailsColors.saveNoteCTADisabledBG.dark,
      saveNoteCTADisabledText: repairDetailsColors.saveNoteCTADisabledText.dark,
      listeningEnabledCTABG: repairDetailsColors.listeningEnabledCTABG.dark,
      listeningEnabledCTAText: repairDetailsColors.listeningEnabledCTAText.dark,
      listeningEnableIconColor:
        repairDetailsColors.listeningEnableIconColor.dark,
      listeningEnableIconBG: repairDetailsColors.listeningEnableIconBG.dark,
      listeningDisableIconColor:
        repairDetailsColors.listeningDisableIconColor.dark,
      listeningDisableIconBG: repairDetailsColors.listeningDisableIconBG.dark,
      mandatoryIconColor: repairDetailsColors.mandatoryIconColor.dark,
      mandatoryIconBG: repairDetailsColors.mandatoryIconBG.dark,
      woStatusTitle: repairDetailsColors.woStatusTitle.dark,
      woStatusDesc: repairDetailsColors.woStatusDesc.dark,
      selectedtabBGColor: repairDetailsColors.selectedtabBGColor.dark,
      selectedtabBorder: repairDetailsColors.selectedtabBorder.dark,
      indirectActivityModalOverlay: indirectActivityColors.modalOverlay.dark,
      indirectActivityConfirmationText:
        indirectActivityColors.confirmationText.dark,
      indirectActivitySelectedActivityBorder:
        indirectActivityColors.selectedActivityBorder.dark,
      indirectActivitySelectedActivityBackground:
        indirectActivityColors.selectedActivityBackground.dark,
      indirectActivitySelectedActivityText:
        indirectActivityColors.selectedActivityText.dark,

      partsSearchResultTitle: repairDetailsColors.partsSearchResultTitle.dark,
      partsFoundBG: repairDetailsColors.partsFoundBG.dark,
      partsFoundBText: repairDetailsColors.partsFoundBText.dark,
      partsResultTitleBorder: repairDetailsColors.partsResultTitleBorder.dark,
      partsResultTitleColor: repairDetailsColors.partsResultTitleColor.dark,
      partsResultStatus: repairDetailsColors.partsResultStatus.dark,
      partsResultSubTitleColor:
        repairDetailsColors.partsResultSubTitleColor.dark,
      partsResultsPartName: repairDetailsColors.partsResultsPartName.dark,
      partsResultsPartDesc: repairDetailsColors.partsResultsPartDesc.dark,
      partsResultDescColor: repairDetailsColors.partsResultDescColor.dark,
      outOfStockColor: repairDetailsColors.outOfStockColor.dark,
      outOfStockBG: repairDetailsColors.outOfStockBG.dark,
      inStockColor: repairDetailsColors.inStockColor.dark,
      inStockBG: repairDetailsColors.inStockBG.dark,
      quantityDisableBorder: repairDetailsColors.quantityDisableBorder.dark,
      quantityEnableBorder: repairDetailsColors.quantityEnableBorder.dark,
      quantityDisableIconColor:
        repairDetailsColors.quantityDisableIconColor.dark,
      quantityDisableTextColor:
        repairDetailsColors.quantityDisableTextColor.dark,
      quantityEnableIconColor: repairDetailsColors.quantityEnableIconColor.dark,
      quantityEnableTextColor: repairDetailsColors.quantityEnableTextColor.dark,
      addDisabledBG: repairDetailsColors.addDisabledBG.dark,
      addDisabledText: repairDetailsColors.addDisabledText.dark,
      addEnabledBorder: repairDetailsColors.addEnabledBorder.dark,
      addEnabledText: repairDetailsColors.addEnabledText.dark,
      closeIconColor: repairDetailsColors.closeIconColor.dark,
      noPartsResultBG: repairDetailsColors.noPartsResultBG.dark,
      noPartsResultBorder: repairDetailsColors.noPartsResultBorder.dark,
      noPartsResultIconBG: repairDetailsColors.noPartsResultIconBG.dark,
      noPartsResultIconText: repairDetailsColors.noPartsResultIconText.dark,
      noPartsResultTitle: repairDetailsColors.noPartsResultTitle.dark,
      noPartsResultDesc: repairDetailsColors.noPartsResultDesc.dark,

      confirmPartsSubTitle: repairDetailsColors.confirmPartsSubTitle.dark,
      header: repairDetailsColors.header.dark,
      partName: repairDetailsColors.partName.dark,
      reqQuantity: repairDetailsColors.reqQuantity.dark,
      partNumberKey: repairDetailsColors.partNumberKey.dark,
      partNumberValue: repairDetailsColors.partNumberValue.dark,
      partNumberValueBorder: repairDetailsColors.partNumberValueBorder.dark,
      partIdKey: repairDetailsColors.partIdKey.dark,
      partIdValue: repairDetailsColors.partIdValue.dark,
      woDetails: repairDetailsColors.woDetails.dark,
      bottomTabsKey: repairDetailsColors.bottomTabsKey.dark,
      bottomTabsvalue: repairDetailsColors.bottomTabsvalue.dark,
      bottomTabsBorder: repairDetailsColors.bottomTabsBorder.dark,
      separatorLineParts: repairDetailsColors.separatorLineParts.dark,
      adddQuantity: repairDetailsColors.adddQuantity.dark,
      addPartsNoteBg: repairDetailsColors.addPartsNoteBg.dark,
      viewNoteBG: repairDetailsColors.viewNoteBG.dark,
      viewNoteInfoBorder: repairDetailsColors.viewNoteInfoBorder.dark,
      addPartsNoteBorder: repairDetailsColors.addPartsNoteBorder.dark,
      addPartsNoteText: repairDetailsColors.addPartsNoteText.dark,
      workOrderStatusDateIcon: workOrderColors.statusDateIcon.dark,
      inputBorder: inputColors.border.dark,
      shopHomeWorkOrderSelectedTextColor:
        shopHomeColors.workOrderSelectedTextColor.dark,
      leastPriorityBGColor: priorityColors.leastPriorityBGColor.dark,
      leastPriorityBorderColor: priorityColors.lowColor.dark,
      pendingBackground: repairDetailsColors.pendingBackground.dark,
      issuedBackground: repairDetailsColors.issuedBackground.dark,
      readyBackground: repairDetailsColors.readyBackground.dark,
      approvedBackground: repairDetailsColors.approvedBackground.dark,
      rejectedBackground: repairDetailsColors.rejectedBackground.dark,
      requestedBackground: repairDetailsColors.requestedBackground.dark,
      onOrderBackground: repairDetailsColors.onOrderBackground.dark,
      pendingBorder: repairDetailsColors.pendingBorder.dark,
      issuedBorder: repairDetailsColors.issuedBorder.dark,
      readyBorder: repairDetailsColors.readyBorder.dark,
      onOrderBorder: repairDetailsColors.onOrderBorder.dark,
      defaultStatusBackground: repairDetailsColors.defaultStatusBackground.dark,
      defaultStatusText: repairDetailsColors.defaultStatusText.dark,
      defaultStatusBorder: repairDetailsColors.defaultStatusBorder.dark,
      addPartsBtnBorder: listViewColors.actionBorder.dark,
      addPartsBtnText: listViewColors.actionText.dark,
      partsAssignedDisabledBG: workOrderColors.partsAssignedDisabledBG.dark,
      partsAssignedDisabledBorder:
        workOrderColors.partsAssignedDisabledBorder.dark,
      partsAssignedDisabledIcon: workOrderColors.partsAssignedDisabledIcon.dark,
      partsAssigneEnabledIcon: workOrderColors.partsAssigneEnabledIcon.dark,
      partsAssigneEnabledBorder: workOrderColors.partsAssigneEnabledBorder.dark,
      partsAssignedEnabledBG: workOrderColors.partsAssignedEnabledBG.dark,
      workOrderTileViewIcon: workOrderColors.tileViewIcon.dark,
      workOrdeViewBorder: shopHomeColors.workOrdeViewBorder.dark,
      workOrderListViewIconColor: workOrderColors.listViewIconColor.dark,
      workOrderTileViewSelectedIcon: workOrderColors.tileViewSelectedIcon.dark,
      workOrderListViewSelectedIconColor:
        workOrderColors.listViewSelectedIconColor.dark,
      workOrderSortIconsBorder: workOrderColors.sortBorderAlt.dark,
      tileViewBorder: tileViewColors.border.dark,
      imageBorder: repairDetailsColors.imageColor.dark,
      viewDetailsTextColor: detailColors.textColor.dark,
      openEyeIcon: eyeColors.openIcon.dark,
      detailLabelTextColor: detailColors.labelTextColor.dark,

      partMessagesBGColor: repairDetailsColors.partMessagesBGColor.dark,
      selectedCountBG: dashboardColors.selecetdCountBG.dark,
      countBG: dashboardColors.countBG.dark,
      unReadMesageBGColor: repairDetailsColors.unReadMesageBGColor.dark,
      ownPartsBorder: repairDetailsColors.ownPartsBorder.dark,
      additionBadgeBG: repairDetailsColors.additionBadgeBG.dark,
      inputPlaceHolder: searchInputColors.placeholder.dark,
      charCountBG: repairDetailsColors.charCountBG.dark,
      inputDisabledBorder: inputColors.disabledBorder.dark,
      inputEditableBorder: inputColors.border.dark,
      partsReservedBGColor: partsStatusColors.reservedBG.dark,
      partsReservedBorderColor: partsStatusColors.reservedBorder.dark,
      meterRangeInfoBG: repairDetailsColors.meterRangeInfoBG.dark,
      meterRangeInfoBorder: repairDetailsColors.meterRangeInfoBorder.dark,
      meterRangeInfoText: repairDetailsColors.meterRangeInfoText.dark,
      updateMeterCTAText: repairDetailsColors.updateMeterCTAText.dark,
      partsViewDisabledBGColor:
        repairDetailsColors.partsViewDisabledBGColor.dark,
      accumulatedHoursExceededColor:
        repairDetailsColors.accumulatedHoursExceededColor.dark,
      meterModalTitleIconColor:
        repairDetailsColors.meterModalTitleIconColor.dark,
      viewAllCTAIconColor: viewAllColors.CTAIcon.dark,
      errorText: createFlowColors.errorText.dark,
      errorBG: createFlowColors.errorBG.dark,
      notesTickMark: repairDetailsColors.notesTickMark.dark,
      taskNameBG: repairDetailsColors.taskNameBG.dark,
      accumulatedErrorText: repairDetailsColors.accumulatedErrorText.dark,
      workOrderFilterSelectedTextBorder:
        workOrderColors.filterSelectedTextBorder.dark,
      tabTextSelectedColor: helperColors.tabTextSelectedColor.dark,
      repairHistoryNoteTitle: repairDetailsColors.repairHistoryNoteTitle.dark,
      openBorderColor: dropdownColors.openBorder.dark,
      selectedDropdownBGColor: dropdownColors.selectedBGColor.dark,
      selectedDropdownTextColor: dropdownColors.selectedTextColor.dark,
      dropdownBGColor: dropdownColors.BGColor.dark,
      dropdownShadowColor: dropdownColors.shadowColor.dark,
      /** Separator line between groups inside the grouped dropdown panel */
      groupedDropdownSeparatorColor: dropdownColors.selectedBGColor.dark,
      dropdownIcon: dropdownColors.icon.dark,
      bGDisableColor: buttonColors.ctaDisableBG.dark,
      bgDisableTitle: buttonColors.badgeDisabledText.dark,
      sortIconColor: commonColors.sortIcon.dark,
      selectedSortIconColor: commonColors.sortSelectedIcon.dark,
      repairHistoryplaceholderText: dropdownColors.textColor.dark
    },
  },

  novaAIScreen: {
    light: {
      bGColor: novaAIColors.BGColor.light,
      novAIShadowColor: novaAIColors.shadowColor.light,
      novaAIBorderColor: novaAIColors.borderColor.light,
      userMessageBGColor: novaAIColors.userMessageBGColor.light,
      userMessageColor: novaAIColors.userMessageColor.light,
      userMessageBorder: novaAIColors.userMessageBorder.light,
      botMessageBGColor: novaAIColors.botMessageBGColor.light,
      botMessageColor: novaAIColors.botMessageColor.light,
      botMessageBorder: novaAIColors.botMessageBorder.light,
      userMessageText: novaAIColors.userMessageText.light,
      botMessageText: novaAIColors.botMessageText.light,
      searchTextInputTopBorderColor:
        novaAIColors.searchTextInputTopBorderColor.light,
      inputTextColor: novaAIColors.inputTextColor.light,
      inputTextBorderColor: novaAIColors.inputTextBorderColor.light,
      footerTextColor: novaAIColors.footerTextColor.light,
      botIconBGColor: novaAIColors.botIconBGColor.light,
      novaAIContextText: novaAIColors.contextText.light,
      novaAIContextSubText: novaAIColors.contextSubText.light,
      cardBorderColor: novaAIColors.cardBorderColor.light,
      cardTextColor: novaAIColors.cardTextColor.light,
      cardExampleTextColor: novaAIColors.cardExampleTextColor.light,
      novaAIIconBGColor: novaAIColors.iconBGColor.light,
      novaAIShadowColor: novaAIColors.shadowColor.light,
      dropdownShadowColor: dropdownColors.shadowColor.light,
      voiceIconGreyColor: novaAIColors.voiceIconGreyColor.light,
      voiceIconBlueColor: novaAIColors.voiceIconBlueColor.light,
      voiceIconGreyFillColor: novaAIColors.voiceIconGreyFillColor.light,
      voiceIconBlueFillColor: novaAIColors.voiceIconBlueFillColor.light,
      waveformBarColor: novaAIColors.waveformBarColor.light,
      listViewBG: listViewColors.BG.light,
      listViewBorder: listViewColors.border.light,
      listViewHeaderText: listViewColors.headerText.light,
      listViewHeaderBG: listViewColors.headerBG.light,
      volumeIconColor: novaAIColors.volumeIconColor.light,
    },
    dark: {
      bGColor: novaAIColors.BGColor.dark,
      novAIShadowColor: novaAIColors.shadowColor.dark,
      novaAIBorderColor: novaAIColors.borderColor.dark,
      userMessageBGColor: novaAIColors.userMessageBGColor.dark,
      userMessageColor: novaAIColors.userMessageColor.dark,
      userMessageBorder: novaAIColors.userMessageBorder.dark,
      botMessageBGColor: novaAIColors.botMessageBGColor.dark,
      botMessageColor: novaAIColors.botMessageColor.dark,
      botMessageBorder: novaAIColors.botMessageBorder.dark,
      userMessageText: novaAIColors.userMessageText.dark,
      botMessageText: novaAIColors.botMessageText.dark,
      searchTextInputTopBorderColor:
        novaAIColors.searchTextInputTopBorderColor.dark,
      inputTextColor: novaAIColors.inputTextColor.dark,
      inputTextBorderColor: novaAIColors.inputTextBorderColor.dark,
      footerTextColor: novaAIColors.footerTextColor.dark,
      botIconBGColor: novaAIColors.botIconBGColor.dark,
      novaAIContextText: novaAIColors.contextText.dark,
      novaAIContextSubText: novaAIColors.contextSubText.dark,
      cardBorderColor: novaAIColors.cardBorderColor.dark,
      cardTextColor: novaAIColors.cardTextColor.dark,
      cardExampleTextColor: novaAIColors.cardExampleTextColor.dark,
      novaAIIconBGColor: novaAIColors.iconBGColor.dark,
      novaAIShadowColor: novaAIColors.shadowColor.dark,
      dropdownShadowColor: dropdownColors.shadowColor.dark,
      voiceIconGreyColor: novaAIColors.voiceIconGreyColor.dark,
      voiceIconBlueColor: novaAIColors.voiceIconBlueColor.dark,
      voiceIconGreyFillColor: novaAIColors.voiceIconGreyFillColor.dark,
      voiceIconBlueFillColor: novaAIColors.voiceIconBlueFillColor.dark,
      waveformBarColor: novaAIColors.waveformBarColor.dark,
      listViewBG: listViewColors.BG.dark,
      listViewBorder: listViewColors.border.dark,
      listViewHeaderText: listViewColors.headerText.dark,
      listViewHeaderBG: listViewColors.headerBG.dark,
      volumeIconColor: novaAIColors.volumeIconColor.dark,
    },
  },

  pagination: {
    light: {
      listPaginationBorder: listPaginationColors.border.light,
      listPaginationEnable: listPaginationColors.enable.light,
      listPaginationDisable: listPaginationColors.disable.light,
      listPaginationButtonText: listPaginationColors.buttonText.light,
      listFooterTextColor: listPaginationColors.footerTextColor.light,
      listFooterCountText: listPaginationColors.footerCountText.light,
      selectedPagination: listPaginationColors.selectedPagination.light,
      dots: listPaginationColors.dots.light,
      pagination: listPaginationColors.pagination.light,
      selectedPaginationText: listPaginationColors.selectedPaginationText.light,
      paginationButtonText: listPaginationColors.buttonText.light,
    },
    dark: {
      listPaginationBorder: listPaginationColors.border.dark,
      listPaginationEnable: listPaginationColors.enable.dark,
      listPaginationDisable: listPaginationColors.disable.dark,
      listPaginationButtonText: listPaginationColors.buttonText.dark,
      listFooterTextColor: listPaginationColors.footerTextColor.dark,
      listFooterCountText: listPaginationColors.footerCountText.dark,
      selectedPagination: listPaginationColors.selectedPagination.dark,
      dots: listPaginationColors.dots.dark,
      pagination: listPaginationColors.pagination.dark,
      selectedPaginationText: listPaginationColors.selectedPaginationText.dark,
      paginationButtonText: listPaginationColors.buttonText.dark,
    },
  },

  shopHomePage: {
    light: {
      mainBackgroundColor: shopHomeColors.mainBackground.light,
      backgroundColor: commonColors.white.light,
      borderColor: shopHomeColors.filterBorderColor.light,
      viewbackground: shopHomeColors.filterBackground.light,
      logoBorder: shopHomeColors.logoBorder.light,
      searchArrowBg: shopHomeColors.searchArrowBg.light,
      headerText: shopHomeColors.headerText.light,
      helpText: shopHomeColors.helpText.light,
      welcomeTitleText: shopHomeColors.welcomeTitleText.light,
      welcomeSubtitleText: shopHomeColors.welcomeSubtitleText.light,
      textColor: shopHomeColors.textColor.light,
      textDarkColor: shopHomeColors.textDarkColor.light,
      cardBackground: shopHomeColors.cardBackground.light,
      cardBorder: uiColors.cardBorder.light,
      iconBackground: shopHomeColors.iconBackground.light,
      technicianNameText: shopHomeColors.technicianNameText.light,
      technicianRoleText: shopHomeColors.technicianRoleText.light,
      technicianLocationText: shopHomeColors.technicianLocationText.light,
      logoBackground: shopHomeColors.logoBackground.light,
      ellipsisBG: shopHomeColors.ellipsisBG.light,
      ellipsisColor: shopHomeColors.ellipsisColor.light,
      logoText: commonColors.black.light,
      helpIcon: helpColors.icon.light,
      shopHomeFilterSelectedTextColor:
        shopHomeColors.filterSelectedTextColor.light,
      shopHomeWorkOrderSortBorder: shopHomeColors.workOrderSortBorder.light,
      shopHomeWorkOrderSortSelectedText:
        shopHomeColors.workOrderSortSelectedText.light,
      shopHomeWorkOrderSelectedTextColor:
        shopHomeColors.workOrderSelectedTextColor.light,
      shopHomeSortIcon: shopHomeColors.sortIcon.light,
      shopHomePageMessageIcon: shopHomeColors.pageMessageIcon.light,
      technicianStatusBGOffline: technicianStatusColors.BGOffline.light,
      technicianStatusTextOffline: technicianStatusColors.textOffline.light,
      technicianStatusBGOnline: technicianStatusColors.BGOnline.light,
      technicianStatusTextOnline: technicianStatusColors.textOnline.light,
      workOrderSortIconColor: workOrderColors.icon.light,
      workOrderSortIconsBorder: workOrderColors.sortBorderAlt.light,
      workOrderTileViewIcon: workOrderColors.tileViewIcon.light,
      workOrderTileViewSelectedIcon: workOrderColors.tileViewSelectedIcon.light,
      workOrderListViewIconColor: workOrderColors.listViewIconColor.light,
      listPaginationBorder: listPaginationColors.border.light,
      listPaginationEnable: listPaginationColors.enable.light,
      listPaginationDisable: listPaginationColors.disable.light,
      listPaginationButtonText: listPaginationColors.buttonText.light,
      listViewBG: listViewColors.BG.light,
      listViewBorder: listViewColors.border.light,
      listViewHeaderText: listViewColors.headerText.light,
      listViewHeaderBG: listViewColors.headerBG.light,
      listFooterTextColor: listViewColors.footerTextColor.light,
      listFooterCountText: listViewColors.footerCountText.light,
      lisViewHeaderBorder: listPaginationColors.border.light,
      workOrderListViewSelectedIconColor:
        workOrderColors.listViewSelectedIconColor.light,
      newUpdateButtonBG: shopHomeColors.newUpdateButtonBG.light,
      newUpdateButtonText: shopHomeColors.newUpdateButtonText.light,
      errorText: createFlowColors.errorText.light,
      errorBG: createFlowColors.errorBG.light,
      sortIconColor: commonColors.sortIcon.light,
      selectedSortIconColor: commonColors.sortSelectedIcon.light,
    },
    dark: {
      mainBackgroundColor: shopHomeColors.mainBackground.dark,
      backgroundColor: commonColors.lightBlack.dark,
      borderColor: shopHomeColors.filterBorderColor.dark,
      viewbackground: shopHomeColors.filterBackground.dark,
      logoBorder: shopHomeColors.logoBorder.dark,
      searchArrowBg: commonColors.lightBlack.dark,
      headerText: commonColors.white.dark,
      helpText: helpColors.icon.dark,
      welcomeTitleText: shopHomeColors.welcomeTitleText.dark,
      welcomeSubtitleText: shopHomeColors.welcomeSubtitleText.dark,
      textColor: shopHomeColors.textColor.dark,
      textDarkColor: shopHomeColors.textDarkColor.dark,
      cardBackground: shopHomeColors.cardBackground.dark,
      cardBorder: uiColors.cardBorder.dark,
      iconBackground: commonColors.shadedGrey.dark,
      technicianNameText: shopHomeColors.technicianNameText.dark,
      technicianRoleText: shopHomeColors.technicianRoleText.dark,
      technicianLocationText: shopHomeColors.technicianLocationText.dark,
      logoBackground: shopHomeColors.logoBackground.dark,
      ellipsisBG: shopHomeColors.ellipsisBG.dark,
      ellipsisColor: shopHomeColors.ellipsisColor.dark,
      logoText: commonColors.white.dark,
      helpIcon: helpColors.icon.dark,
      shopHomeFilterSelectedTextColor:
        shopHomeColors.filterSelectedTextColor.dark,
      shopHomeWorkOrderSortBorder: shopHomeColors.workOrderSortBorder.dark,
      shopHomeWorkOrderSortSelectedText:
        shopHomeColors.workOrderSortSelectedText.dark,
      shopHomeWorkOrderSelectedTextColor:
        shopHomeColors.workOrderSelectedTextColor.dark,
      shopHomeSortIcon: shopHomeColors.sortIcon.dark,
      shopHomePageMessageIcon: shopHomeColors.pageMessageIcon.dark,
      technicianStatusBGOffline: technicianStatusColors.BGOffline.dark,
      technicianStatusTextOffline: technicianStatusColors.textOffline.dark,
      technicianStatusBGOnline: technicianStatusColors.BGOnline.dark,
      technicianStatusTextOnline: technicianStatusColors.textOnline.dark,
      workOrderSortIconsBorder: workOrderColors.sortBorder.dark,
      workOrderTileViewIcon: workOrderColors.tileViewIcon.dark,
      workOrdeViewBorder: shopHomeColors.workOrdeViewBorder.dark,
      workOrderListViewIconColor: workOrderColors.listViewIconColor.dark,
      workOrderFilterBorder: workOrderColors.filterBorder.dark,
      listPaginationBorder: listPaginationColors.border.dark,
      listPaginationEnable: listPaginationColors.enable.dark,
      listPaginationDisable: listPaginationColors.disable.dark,
      listPaginationButtonText: listPaginationColors.buttonText.dark,
      listViewBG: listViewColors.BG.dark,
      listViewBorder: listViewColors.border.dark,
      listViewHeaderText: listViewColors.headerText.dark,
      listViewHeaderBG: listViewColors.headerBG.dark,
      listFooterTextColor: listViewColors.footerTextColor.dark,
      listFooterCountText: listViewColors.footerCountText.dark,
      lisViewHeaderBorder: listPaginationColors.border.dark,
      workOrderTileViewSelectedIcon: workOrderColors.tileViewSelectedIcon.dark,
      workOrderListViewSelectedIconColor:
        workOrderColors.listViewSelectedIconColor.dark,
      newUpdateButtonBG: shopHomeColors.newUpdateButtonBG.dark,
      newUpdateButtonText: shopHomeColors.newUpdateButtonText.dark,
      errorText: createFlowColors.errorText.dark,
      errorBG: createFlowColors.errorBG.dark,
      sortIconColor: commonColors.sortIcon.dark,
      selectedSortIconColor: commonColors.sortSelectedIcon.dark,
    },
  },

  header: {
    light: {
      backgroundColor: commonColors.headerBG.light,
      borderColor: shopHomeColors.filterBorderColor.light,
      logoBorder: shopHomeColors.logoBorder.light,
      logoText: commonColors.black.light,
      helpText: helpColors.icon.light,
      statusText: helpColors.subText.light,
      helpIcon: helpColors.icon.light,
      shopHomeLogoutCTABorder: shopHomeColors.logoutCTABorder.light,
      shopHomeLogoutCTAIcon: shopHomeColors.logoutCTAIcon.light,
      shopHomeLogoutCTAText: shopHomeColors.logoutCTAText.light,
      languageIcon: languageColors.icon.light,
      languageSelectedText: languageColors.selectedText.light,
      shopHomeLanguageIconColor: shopHomeColors.languageIconColor.light,
      headerIndirectActivityIconColor: indirectActivityColors.iconColor.light,
      IndirectActivityCTABorder: indirectActivityColors.CTABorder.light,
      IndirectActivityCTAText: indirectActivityColors.CTAText.light,
      shadowColor: uiColors.shadowColor.light,
      selectedShopBGColor: selectedShopColors.BG.light,
      selectedShopTextColor: selectedShopColors.textColor.light,
      profileBGColor: profileMenuColors.BGColor.light,
      profileTextColor: profileMenuColors.textColor.light,
      menuLayoutDivider: menuLayoutColors.divider.light,
      quickAssitIconColor: quickAssitColors.iconColor.light,
      quickAssitCTABorder: quickAssitColors.CTABorder.light,
      quickAssitCTAText: quickAssitColors.CTAText.light,
      shiftStatus: commonColors.shiftStatus.light,
      CTAEnabledBG: repairDetailsColors.saveNoteCTAEnabledBG.light,
      CTAEnabledBGText: repairDetailsColors.saveNoteCTAEnabledBGText.light,
      CTADisabledBG: repairDetailsColors.saveNoteCTADisabledBG.light,
      CTADisabledText: repairDetailsColors.saveNoteCTADisabledText.light,
    },
    dark: {
      backgroundColor: commonColors.headerBG.dark,
      borderColor: shopHomeColors.filterBorderColor.dark,
      logoBorder: shopHomeColors.logoBorder.dark,
      logoText: commonColors.white.dark,
      helpText: helpColors.icon.dark,
      statusText: helpColors.subText.dark,
      helpIcon: helpColors.icon.dark,
      shopHomeLogoutCTABorder: shopHomeColors.logoutCTABorder.dark,
      shopHomeLogoutCTAIcon: shopHomeColors.logoutCTAIcon.dark,
      shopHomeLogoutCTAText: shopHomeColors.logoutCTAText.dark,
      languageIcon: languageColors.icon.dark,
      languageSelectedText: languageColors.selectedText.dark,
      shopHomeLanguageIconColor: shopHomeColors.languageIconColor.dark,
      headerIndirectActivityIconColor: indirectActivityColors.iconColor.dark,
      IndirectActivityCTABorder: indirectActivityColors.CTABorder.dark,
      IndirectActivityCTAText: indirectActivityColors.CTAText.dark,
      shadowColor: uiColors.shadowColor.dark,
      selectedShopBGColor: selectedShopColors.BG.dark,
      selectedShopTextColor: selectedShopColors.textColor.dark,
      profileBGColor: profileMenuColors.BGColor.dark,
      profileTextColor: profileMenuColors.textColor.dark,
      menuLayoutDivider: menuLayoutColors.divider.dark,
      quickAssitIconColor: quickAssitColors.iconColor.dark,
      quickAssitCTABorder: quickAssitColors.CTABorder.dark,
      quickAssitCTAText: quickAssitColors.CTAText.dark,
      shiftStatus: commonColors.shiftStatus.dark,
      CTAEnabledBG: repairDetailsColors.saveNoteCTAEnabledBG.dark,
      CTAEnabledBGText: repairDetailsColors.saveNoteCTAEnabledBGText.dark,
      CTADisabledBG: repairDetailsColors.saveNoteCTADisabledBG.dark,
      CTADisabledText: repairDetailsColors.saveNoteCTADisabledText.dark,
    },
  },

  pinAuthentication: {
    light: {
      overlayBackground: pinAuthColors.overlayBackground.light,
      backgroundColor: pinAuthColors.background.light,
      titleColor: pinAuthColors.titleColor.light,
      subtitleColor: pinAuthColors.subtitleColor.light,
      technicianNameColor: pinAuthColors.technicianNameColor.light,
      technicianRoleColor: pinAuthColors.technicianRoleColor.light,
      technicianLocationColor: pinAuthColors.technicianLocationColor.light,
      iconBackground: pinAuthColors.iconBackground.light,
      pinInputBorder: pinAuthColors.pinInputBorder.light,
      pinInputText: pinAuthColors.pinInputText.light,
      pinInputBG: pinInputColors.BG.light,
      pinInputTextBorder: pinInputColors.border.light,
      pinInputErrorBorder: pinAuthColors.pinErrorInputBorder.light,
      pinInputErrorText: pinAuthColors.pinErrorInputText.light,
      pinInputErrorBG: pinInputColors.errorBG.light,
      pinAuthTechniciaInfoBG: pinAuthColors.techniciaInfoBG.light,
      pinAuthTechniciaInfoBorder: pinAuthColors.techniciaInfoBorder.light,
      pinAuthBackCTABGColor: pinAuthColors.backCTABGColor.light,
      pinAuthBackCTABorderColor: pinAuthColors.backCTABorderColor.light,
      pinAuthIconBGColor: pinAuthColors.iconBGColor.light,
      pinAuthBorderColor: pinAuthColors.pinAuthBorderColor.light,
      pinAuthCloseIconColor: pinAuthColors.closeIconColor.light,
      openEyeIcon: eyeColors.openIcon.light,
      closeEyeIcon: eyeColors.closeIcon.light,
      pinAuthenticationEyeText: pinAuthColors.authenticationEyeText.light,
      cancelBtnBorder: createFlowColors.cancelBtnBorder.light,
      cancelBtnText: createFlowColors.cancelBtnText.light,
    },
    dark: {
      overlayBackground: pinAuthColors.overlayBackground.dark,
      backgroundColor: pinAuthColors.background.dark,
      titleColor: pinAuthColors.titleColor.dark,
      subtitleColor: pinAuthColors.subtitleColor.dark,
      technicianNameColor: pinAuthColors.technicianNameColor.dark,
      technicianRoleColor: pinAuthColors.technicianRoleColor.dark,
      technicianLocationColor: pinAuthColors.technicianLocationColor.dark,
      iconBackground: pinAuthColors.iconBackground.dark,
      pinInputBorder: pinAuthColors.pinInputBorder.dark,
      pinInputText: pinAuthColors.pinInputText.dark,
      pinInputBG: pinInputColors.BG.dark,
      pinInputTextBorder: pinInputColors.border.dark,
      pinInputErrorBorder: pinAuthColors.pinErrorInputBorder.dark,
      pinInputErrorText: pinAuthColors.pinErrorInputText.dark,
      pinInputErrorBG: pinInputColors.errorBG.dark,
      pinAuthTechniciaInfoBG: pinAuthColors.techniciaInfoBG.dark,
      pinAuthTechniciaInfoBorder: pinAuthColors.techniciaInfoBorder.dark,
      pinAuthBackCTABGColor: pinAuthColors.backCTABGColor.dark,
      pinAuthBackCTABorderColor: pinAuthColors.backCTABorderColor.dark,
      pinAuthIconBGColor: pinAuthColors.iconBGColor.dark,
      pinAuthBorderColor: pinAuthColors.pinAuthBorderColor.dark,
      pinAuthCloseIconColor: pinAuthColors.closeIconColor.dark,
      openEyeIcon: eyeColors.openIcon.dark,
      closeEyeIcon: eyeColors.closeIcon.dark,
      pinAuthenticationEyeText: pinAuthColors.authenticationEyeText.dark,
      cancelBtnBorder: createFlowColors.cancelBtnBorder.dark,
      cancelBtnText: createFlowColors.cancelBtnText.dark,
    },
  },

  cards: {
    light: {
      scoreBoardCardBG: scoreBoardColors.scoreBoardCard.light,
      scoreBoardCardBorder: scoreBoardColors.cardBorder.light,
      scoreBoardCardTitle: scoreBoardColors.cardTitle.light,
      scoreBoardCardCount: scoreBoardColors.cardCount.light,
    },
    dark: {
      scoreBoardCardBG: scoreBoardColors.scoreBoardCard.dark,
      scoreBoardCardBorder: commonColors.viewBorder.dark,
      scoreBoardCardTitle: scoreBoardColors.cardTitle.dark,
      scoreBoardCardCount: scoreBoardColors.cardCount.dark,
    },
  },

  checkbox: {
    light: {
      borderColor: checkboxColors.border.light,
      labelColor: checkboxColors.label.light,
      // ── Normal (unchecked) ──────────────────────────────────────────────
      listUnSelectedBorder: checkboxColors.unSelectedBorderColor.light,
      listUnSelectedBG: checkboxColors.unSelectedBGColor.light,
      // ── Checked ─────────────────────────────────────────────────────────
      listSelectedBorder: checkboxColors.selectedBorderColor.light,
      listSelectedBG: checkboxColors.selectedBGColor.light,
      // ── Hover ───────────────────────────────────────────────────────────
      hoverBG: checkboxColors.hoverBGColor.light,
      hoverBorder: checkboxColors.hoverBorderColor.light,
      // ── Pressed ─────────────────────────────────────────────────────────
      pressedBG: checkboxColors.pressedBGColor.light,
      pressedBorder: checkboxColors.pressedBorderColor.light,
      // ── Disabled (unchecked) ────────────────────────────────────────────
      disabledBG: checkboxColors.disabledBGColor.light,
      disabledBorder: checkboxColors.disabledBorderColor.light,
      // ── Checked + Disabled ──────────────────────────────────────────────
      checkedDisabledBG: checkboxColors.checkedDisabledBGColor.light,
      checkedDisabledBorder: checkboxColors.checkedDisabledBorderColor.light,
      // ── Tick icon ───────────────────────────────────────────────────────
      tickMarkIconColor: checkboxColors.tickMarkIconColor.light,
    },
    dark: {
      borderColor: checkboxColors.border.dark,
      labelColor: checkboxColors.label.dark,
      // ── Normal (unchecked) ──────────────────────────────────────────────
      listUnSelectedBorder: checkboxColors.unSelectedBorderColor.dark,
      listUnSelectedBG: checkboxColors.unSelectedBGColor.dark,
      // ── Checked ─────────────────────────────────────────────────────────
      listSelectedBorder: checkboxColors.selectedBorderColor.dark,
      listSelectedBG: checkboxColors.selectedBGColor.dark,
      // ── Hover ───────────────────────────────────────────────────────────
      hoverBG: checkboxColors.hoverBGColor.dark,
      hoverBorder: checkboxColors.hoverBorderColor.dark,
      // ── Pressed ─────────────────────────────────────────────────────────
      pressedBG: checkboxColors.pressedBGColor.dark,
      pressedBorder: checkboxColors.pressedBorderColor.dark,
      // ── Disabled (unchecked) ────────────────────────────────────────────
      disabledBG: checkboxColors.disabledBGColor.dark,
      disabledBorder: checkboxColors.disabledBorderColor.dark,
      // ── Checked + Disabled ──────────────────────────────────────────────
      checkedDisabledBG: checkboxColors.checkedDisabledBGColor.dark,
      checkedDisabledBorder: checkboxColors.checkedDisabledBorderColor.dark,
      // ── Tick icon ───────────────────────────────────────────────────────
      tickMarkIconColor: checkboxColors.tickMarkIconColor.dark,
    },
  },

  button: {
    light: {
      bGDisableColor: buttonColors.ctaDisableBG.light,
      textDisableColor: buttonColors.ctaDisableText.light,
      ctaDisableText: buttonColors.ctaDisableText.light,
      primaryBG: buttonColors.primary.light,
      primartText: buttonColors.primaryText.light,
      /** Hover/pressed fallback — overridden per client theme via themeResolver */
      primaryHoverBG: buttonColors.primary.light,
      primaryPressedBG: buttonColors.primary.light,
      secondaryText: buttonColors.secondaryText.light,
      secondaryBG: buttonColors.secondary.light,
      secondaryBorderColor: buttonColors.secondaryBorderColor.light,
      secondaryHoverBG: buttonColors.secondaryHoverBG.light,
      secondaryPressedBG: buttonColors.secondaryPressedBG.light,
      borderColor: buttonColors.borderColor.light,
      CTAIcon: buttonColors.CTAIcon.light,
      CTAText: buttonColors.CTAText.light,
      bGEnableColor: buttonColors.primary.light,
      textEnableColor: buttonColors.secondaryText.light,
      subTitleBadgeBg: buttonColors.subTitleBadgeBg.light,
      subTitleBadgeText: buttonColors.subTitleBadgeText.light,
    },
    dark: {
      bGDisableColor: buttonColors.ctaDisableBG.dark,
      textDisableColor: buttonColors.ctaDisableText.dark,
      ctaDisableText: buttonColors.ctaDisableText.dark,
      bGEnableColor: buttonColors.primary.dark,
      textEnableColor: buttonColors.primaryText.dark,
      borderColor: buttonColors.borderColor.dark,
      CTAIcon: buttonColors.CTAIcon.dark,
      CTAText: buttonColors.CTAText.dark,
      primaryBG: buttonColors.primary.dark,
      primartText: buttonColors.primaryText.dark,
      /** Hover/pressed fallback — overridden per client theme via themeResolver */
      primaryHoverBG: buttonColors.primary.dark,
      primaryPressedBG: buttonColors.primary.dark,
      secondaryText: buttonColors.secondaryText.dark,
      secondaryBG: buttonColors.secondary.dark,
      secondaryBorderColor: buttonColors.secondaryBorderColor.dark,
      secondaryHoverBG: buttonColors.secondaryHoverBG.dark,
      secondaryPressedBG: buttonColors.secondaryPressedBG.dark,
      subTitleBadgeBg: buttonColors.subTitleBadgeBg.dark,
      subTitleBadgeText: buttonColors.subTitleBadgeText.dark,
    },
  },

  loader: {
    light: {},
    dark: {},
  },

  searchInput: {
    light: {
      backgroundColor: searchInputColors.input.light,
      borderColor: searchInputColors.border.light,
      inputColor: searchInputColors.inputColor.light,
      searchIconColor: searchInputColors.iconColor.light,
      searchResultColor: searchInputColors.resultColor.light,
      searchResultBGColor: searchInputColors.resultBGColor.light,
      inputPlaceHolder: searchInputColors.placeholder.light,
      pinAuthCloseIconColor: pinAuthColors.closeIconColor.light,
    },
    dark: {
      backgroundColorLight: searchInputColors.input.dark,
      borderColor: searchInputColors.border.dark,
      inputColor: searchInputColors.inputColor.dark,
      searchIconColor: searchInputColors.iconColor.dark,
      searchResultColor: searchInputColors.resultColor.dark,
      searchResultBGColor: searchInputColors.resultBGColor.dark,
      inputPlaceHolder: searchInputColors.placeholder.dark,
      pinAuthCloseIconColor: pinAuthColors.closeIconColor.dark,
    },
  },
  profile: {
    light: {
      online: profileColors.online.light,
      offline: profileColors.logout.light,
      BGColor: profileColors.BGColor.light,
      textColor: profileColors.textColor.light,
      background: commonColors.headerBG.light,
      dropdownBG: profileColors.dropdownBG.light,
      dropdownShadow: profileColors.dropdownShadow.light,
      dropdownBorder: profileColors.dropdownBorder.light,
      dropdownSeparator: profileColors.dropdownSeparator.light,
      dropdownUserName: profileColors.dropdownUserName.light,
      dropdownUserId: profileColors.dropdownUserId.light,
      dropdownValues: profileColors.dropdownValues.light,
      dropdownValuesIcon: profileColors.dropdownValuesIcon.light,
      logout: profileColors.logout.light,
    },
    dark: {
      online: profileColors.online.dark,
      offline: profileColors.logout.dark,
      BGColor: profileColors.BGColor.dark,
      textColor: profileColors.textColor.dark,
      background: commonColors.headerBG.dark,
      dropdownBG: profileColors.dropdownBG.dark,
      dropdownShadow: profileColors.dropdownShadow.dark,
      dropdownBorder: profileColors.dropdownBorder.dark,
      dropdownSeparator: profileColors.dropdownSeparator.dark,
      dropdownUserName: profileColors.dropdownUserName.dark,
      dropdownUserId: profileColors.dropdownUserId.dark,
      dropdownValues: profileColors.dropdownValues.dark,
      dropdownValuesIcon: profileColors.dropdownValuesIcon.dark,
      logout: profileColors.logout.dark,
    },
  },
  errorToast: {
    light: {
      border: errorToast.border.light,
      bgColor: errorToast.bgColor.light,
      closeIconColor: errorToast.closeIconColor.light,
      closeCTAColor: errorToast.closeCTAColor.light,
      successBorder: errorToast.successBorder.light,
      successBgColor: errorToast.successBgColor.light,
      successCloseIconColor: errorToast.successCloseIconColor.light,
      successCloseCTAColor: errorToast.successCloseCTAColor.light,
      text: errorToast.text.light,
    },
    dark: {
      border: errorToast.border.dark,
      bgColor: errorToast.bgColor.dark,
      closeIconColor: errorToast.closeIconColor.dark,
      closeCTAColor: errorToast.closeCTAColor.dark,
      text: errorToast.text.dark,
      successBorder: errorToast.successBorder.dark,
      successBgColor: errorToast.successBgColor.dark,
      successCloseIconColor: errorToast.successCloseIconColor.dark,
      successCloseCTAColor: errorToast.successCloseCTAColor.dark,
    },
  },
  feedbackState: {
    light: {
      iconColor: feedback.icon.light,
      title: feedback.title.light,
      subTitle: feedback.subTitle.light,
    },
    dark: {
      iconColor: feedback.icon.dark,
      title: feedback.title.dark,
      subTitle: feedback.subTitle.dark,
    },
  },
  tooltip: {
    light: {
      tooltipBackground: tooltipColors.tooltipBackground.light,
      tooltipText: tooltipColors.tooltipText.light,
      text: tooltipColors.text.light,
    },
    dark: {
      tooltipBackground: tooltipColors.tooltipBackground.dark,
      tooltipText: tooltipColors.tooltipText.dark,
      text: tooltipColors.text.dark,
    },
  },
  help: {
    light: {
      mainBackgroundColor: helperColors.mainBackground.light,
      borderColor: helperColors.filterBorderColor.light,
      viewbackground: helperColors.filterBackground.light,
      topCardBorder: helperColors.topCardBorder.light,
      topTitle: helperColors.topTitle.light,
      topSubTitle: helperColors.topSubTitle.light,
      textColor: helperColors.textColor.light,
      notesBorder: helperColors.notesBorder.light,
      notesFilterUnselectBGColor: helperColors.notesFilterUnselectBGColor.light,
      notesTypeColor: helperColors.notesTypeColor.light,
      notesDescColor: helperColors.notesDescColor.light,
      notesUserColor: helperColors.notesUserColor.light,
      notesUserBGColor: helperColors.notesUserBGColor.light,
      notesUserDate: helperColors.notesUserDate.light,
      titleCardActionColor: helperColors.titleCardActionColor.light,
      divider: helperColors.divider.light,
      separatorColor: helperColors.separatorColor.light,
      workOrderFilterSelectedTextBorder:
        helperColors.workOrderFilterSelectedTextBorder.light,
      tabTextColor: helperColors.tabTextColor.light,
      tabTextSelectedColor: helperColors.tabTextSelectedColor.light,
      closeModalIconColor: helperColors.closeModalIconColor.light,
      notedModalBG: helperColors.notedModalBG.light,
      notesModalBorder: helperColors.notesModalBorder.light,
      noteHeaderSeparator: helperColors.noteHeaderSeparator.light,
      notedModalMainTitle: helperColors.notedModalMainTitle.light,
      cancelCTABorder: helperColors.cancelCTABorder.light,
      cancelCTAText: helperColors.cancelCTAText.light,
      tabIconSelectedColor: helperColors.tabIconSelectedColor.light,
      tabIconColor: helperColors.tabIconColor.light,
    },
    dark: {
      mainBackgroundColor: helperColors.mainBackground.dark,
      borderColor: helperColors.filterBorderColor.dark,
      viewbackground: helperColors.filterBackground.dark,
      topCardBorder: helperColors.topCardBorder.dark,
      topTitle: helperColors.topSubTitle.dark,
      topSubTitle: helperColors.topSubTitle.dark,
      textColor: helperColors.textColor.dark,
      notesBorder: helperColors.notesBorder.dark,
      notesFilterUnselectBGColor: helperColors.notesFilterUnselectBGColor.dark,
      notesTypeColor: helperColors.notesTypeColor.dark,
      notesDescColor: helperColors.notesDescColor.dark,
      notesUserColor: helperColors.notesUserColor.dark,
      notesUserBGColor: helperColors.notesUserBGColor.dark,
      notesUserDate: helperColors.notesUserDate.dark,
      titleCardActionColor: helperColors.titleCardActionColor.dark,
      divider: helperColors.divider.dark,
      separatorColor: helperColors.separatorColor.dark,
      workOrderFilterSelectedTextBorder:
        helperColors.workOrderFilterSelectedTextBorder.dark,
      tabTextColor: helperColors.tabTextColor.dark,
      tabTextSelectedColor: helperColors.tabTextSelectedColor.dark,
      closeModalIconColor: helperColors.closeModalIconColor.dark,
      notedModalBG: helperColors.notedModalBG.dark,
      notesModalBorder: helperColors.notesModalBorder.dark,
      noteHeaderSeparator: helperColors.noteHeaderSeparator.dark,
      notedModalMainTitle: helperColors.notedModalMainTitle.dark,
      cancelCTABorder: helperColors.cancelCTABorder.dark,
      cancelCTAText: helperColors.cancelCTAText.dark,
      tabIconSelectedColor: helperColors.tabIconSelectedColor.dark,
      tabIconColor: helperColors.tabIconColor.dark,
    },
  },

  stepper: {
    light: {
      background: createFlowColors.stepperBackground.light,
      activeCircleBG: createFlowColors.stepperActiveCircleBG.light,
      activeCircleBorder: createFlowColors.stepperActiveCircleBorder.light,
      activeCircleText: createFlowColors.stepperActiveCircleText.light,
      activeLabel: createFlowColors.stepperActiveLabel.light,
      completedCircleBG: createFlowColors.stepperCompletedCircleBG.light,
      completedCircleBorder:
        createFlowColors.stepperCompletedCircleBorder.light,
      completedCircleText: createFlowColors.stepperCompletedCircleText.light,
      completedLabel: createFlowColors.stepperCompletedLabel.light,
      inactiveCircleBG: createFlowColors.stepperInactiveCircleBG.light,
      inactiveCircleBorder: createFlowColors.stepperInactiveCircleBorder.light,
      inactiveCircleText: createFlowColors.stepperInactiveCircleText.light,
      inactiveLabel: createFlowColors.stepperInactiveLabel.light,
      connectorActive: createFlowColors.stepperConnectorActive.light,
      connectorInactive: createFlowColors.stepperConnectorInactive.light,
      stepperBorder: createFlowColors.stepperBorder.light,
    },
    dark: {
      background: createFlowColors.stepperBackground.dark,
      activeCircleBG: createFlowColors.stepperActiveCircleBG.dark,
      activeCircleBorder: createFlowColors.stepperActiveCircleBorder.dark,
      activeCircleText: createFlowColors.stepperActiveCircleText.dark,
      activeLabel: createFlowColors.stepperActiveLabel.dark,
      completedCircleBG: createFlowColors.stepperCompletedCircleBG.dark,
      completedCircleBorder: createFlowColors.stepperCompletedCircleBorder.dark,
      completedCircleText: createFlowColors.stepperCompletedCircleText.dark,
      completedLabel: createFlowColors.stepperCompletedLabel.dark,
      inactiveCircleBG: createFlowColors.stepperInactiveCircleBG.dark,
      inactiveCircleBorder: createFlowColors.stepperInactiveCircleBorder.dark,
      inactiveCircleText: createFlowColors.stepperInactiveCircleText.dark,
      inactiveLabel: createFlowColors.stepperInactiveLabel.dark,
      connectorActive: createFlowColors.stepperConnectorActive.dark,
      connectorInactive: createFlowColors.stepperConnectorInactive.dark,
      stepperBorder: createFlowColors.stepperBorder.dark,
    },
  },

  createWorkOrderFlow: {
    light: {
      pageBackground: createFlowColors.pageBackground.light,
      breadcrumbText: createFlowColors.pageBreadcrumbText.light,
      breadcrumbActiveText: createFlowColors.pageBreadcrumbActiveText.light,
      separatorText: createFlowColors.pageSeparatorText.light,
      formCardBackground: createFlowColors.formCardBackground.light,
      formCardBorder: createFlowColors.formCardBorder.light,
      formCardShadow: createFlowColors.formCardShadow.light,
      formTitle: createFlowColors.formTitle.light,
      formSubTitle: createFlowColors.formSubTitle.light,
      formLabel: createFlowColors.formLabel.light,
      addNoteText: createFlowColors.addNoteText.light,
      warrantyText: createFlowColors.warrantyText.light,
      formMandatory: createFlowColors.formMandatory.light,
      formInputBG: createFlowColors.formInputBG.light,
      formInputBorder: createFlowColors.formInputBorder.light,
      formInputText: createFlowColors.formInputText.light,
      formInputPlaceholder: createFlowColors.formInputPlaceholder.light,
      verifyBtnDisabledBG: createFlowColors.verifyBtnDisabledBG.light,
      verifyBtnDisabledText: createFlowColors.verifyBtnDisabledText.light,
      verifyBtnEnabledBG: createFlowColors.verifyBtnEnabledBG.light,
      verifyBtnEnabledText: createFlowColors.verifyBtnEnabledText.light,
      verifyBtnBorder: createFlowColors.verifyBtnBorder.light,
      cancelBtnBorder: createFlowColors.cancelBtnBorder.light,
      cancelBtnText: createFlowColors.cancelBtnText.light,
      cancelBtnBG: createFlowColors.cancelBtnBG.light,
      nextBtnDisabledBG: createFlowColors.nextBtnDisabledBG.light,
      nextBtnDisabledText: createFlowColors.nextBtnDisabledText.light,
      nextBtnEnabledBG: createFlowColors.nextBtnEnabledBG.light,
      nextBtnEnabledText: createFlowColors.nextBtnEnabledText.light,
      footerBackground: createFlowColors.footerBackground.light,
      footerBorder: createFlowColors.footerBorder.light,
      // Asset Details Step 2
      assetDetailsSectionTitle: createFlowColors.assetDetailsSectionTitle.light,
      assetDetailsDivider: createFlowColors.assetDetailsDivider.light,
      assetDetailsLabelText: createFlowColors.assetDetailsLabelText.light,
      assetDetailsValueText: createFlowColors.assetDetailsValueText.light,
      assetDetailsImageBorder: createFlowColors.assetDetailsImageBorder.light,
      openWorkOrdersBadgeBG: createFlowColors.openWorkOrdersBadgeBG.light,
      openWorkOrdersBadgeText: createFlowColors.openWorkOrdersBadgeText.light,
      openWorkOrdersBadgeBorder:
        createFlowColors.openWorkOrdersBadgeBorder.light,
      tableHeaderBG: createFlowColors.tableHeaderBG.light,
      tableHeaderText: createFlowColors.tableHeaderText.light,
      tableRowText: createFlowColors.tableRowText.light,
      tableRowBorder: createFlowColors.tableRowBorder.light,
      tableNegativeDue: createFlowColors.tableNegativeDue.light,
      priorityUrgentBG: createFlowColors.priorityUrgentBG.light,
      priorityUrgentText: createFlowColors.priorityUrgentText.light,
      priorityUrgentBorder: createFlowColors.priorityUrgentBorder.light,
      priorityHighBG: createFlowColors.priorityHighBG.light,
      priorityHighText: createFlowColors.priorityHighText.light,
      priorityHighBorder: createFlowColors.priorityHighBorder.light,
      priorityLowBG: createFlowColors.priorityLowBG.light,
      priorityLowText: createFlowColors.priorityLowText.light,
      priorityLowBorder: createFlowColors.priorityLowBorder.light,
      expandRowBG: createFlowColors.expandRowBG.light,
      expandRowBorder: createFlowColors.expandRowBorder.light,
      expandIconColor: createFlowColors.expandIconColor.light,
      expandDueDateText: createFlowColors.expandDueDateText.light,
      // Contacts section
      contactTabActiveBG: createFlowColors.contactTabActiveBG.light,
      contactTabActiveText: createFlowColors.contactTabActiveText.light,
      contactTabActiveBadgeBG: createFlowColors.contactTabActiveBadgeBG.light,
      contactTabActiveBadgeText:
        createFlowColors.contactTabActiveBadgeText.light,
      contactTabInactiveBG: createFlowColors.contactTabInactiveBG.light,
      contactTabInactiveText: createFlowColors.contactTabInactiveText.light,
      contactTabInactiveBadgeBG:
        createFlowColors.contactTabInactiveBadgeBG.light,
      contactTabInactiveBadgeText:
        createFlowColors.contactTabInactiveBadgeText.light,
      contactTabBorder: createFlowColors.contactTabBorder.light,
      contactCardRoleText: createFlowColors.contactCardRoleText.light,
      contactCardNameText: createFlowColors.contactCardNameText.light,
      contactCardInfoText: createFlowColors.contactCardInfoText.light,
      listViewHeaderBG: listViewColors.headerBG.light,
      listViewBorder: listViewColors.border.light,
      listViewHeaderText: listViewColors.headerText.light,
      workOrderStatusTextBG: workOrderColors.workOrderStatusTextBG.light,
      workOrderStatusSelectedTextBG:
        workOrderColors.workOrderStatusSelectedTextBG.light,
      tileCardCTASelectedBorder:
        repairDetailsColors.tileCardCTASelectedBorder.light,
      tabTextColor: dashboardColors.tabTextColor.light,
      selectedCountBG: dashboardColors.selecetdCountBG.light,
      countBG: dashboardColors.countBG.light,
      selectedCountTextColor: dashboardColors.selectedCountTextColor.light,
      countTextColor: dashboardColors.countTextColor.light,
      calenderBorder: workOrderColors.sortBorderAlt.light,
      woTableHeaderText: createFlowColors.woTableHeaderText.light,
      listBorder: listViewColors.lightBorder.light,
      actionBG: createFlowColors.actionBG.light,
      actionBorder: createFlowColors.actionBorder.light,
      successTitle: createFlowColors.successTitle.light,
      successSubTitle: createFlowColors.successSubTitle.light,
      lightBorder: createFlowColors.lightBorder.light,
      breadCrumbLight: createFlowColors.breadCrumbLight.light,
      workOrderTileViewIcon: workOrderColors.tileViewIcon.light,
      workOrdeViewBorder: shopHomeColors.workOrdeViewBorder.light,
      workOrderListViewIconColor: workOrderColors.listViewIconColor.light,
      workOrderTileViewSelectedIcon: workOrderColors.tileViewSelectedIcon.light,
      workOrderListViewSelectedIconColor:
        workOrderColors.listViewSelectedIconColor.light,
      workOrderSortIconsBorder: workOrderColors.sortBorderAlt.light,
      // Card colors (mirror DashboardScreen)
      workOrderIdBGColor: workOrderColors.idBGColor.light,
      workOrderIdTextColor: workOrderColors.idTextColor.light,
      workOrderIdValueColor: workOrderColors.idValueColor.light,
      tileViewBorder: tileViewColors.border.light,
      listViewBG: listViewColors.BG.light,
      descriptionTextColor: detailColors.descriptionTextColor.light,
      repairIdTextColor: detailColors.repairIdTextColor.light,
      repairIdValueColor: detailColors.repairIdValueColor.light,
      cardSeparatorColor: detailColors.cardSeparator.light,
      viewDetailsTextColor: detailColors.textColor.light,
      detailLabelTextColor: detailColors.labelTextColor.light,
      viewDetailsBorderColor: detailColors.borderColor.light,
      ctaColor: detailColors.ctaColor.light,
      inCompletedRepairs: statusColors.closedColorBG.light,
      inCompletedRepairsText: statusColors.closedColorText.light,
      selectedCTA: createFlowColors.selectedCTA.light,
      selectCTAText: createFlowColors.selectedCTAText.light,
      listSelectedBorder: checkboxColors.selectedBorderColor.light,
      listUnSelectedBorder: checkboxColors.unSelectedBorderColor.light,
      listSelectedBG: checkboxColors.selectedBGColor.light,
      listUnSelectedBG: checkboxColors.unSelectedBGColor.light,
      warrantyModalOverlay: createFlowColors.warrantyModalOverlay.light,
      closeModalIconColor: repairDetailsColors.closeModalIconColor.light,
      expandedWarrantyBG: createFlowColors.expandedWarrantyBG.light,
      errorText: createFlowColors.errorText.light,
      errorBG: createFlowColors.errorBG.light,
      inputPlaceHolder: inputColors.placeholder.light,
      meterInputBorder: createFlowColors.meterInputBorder.light,
      workOrderDropdownPlaceholderText:
        workOrderColors.dropdownPlaceholderText.light,
      calendarIcon: createFlowColors.calendarIcon.light,
      charactersText: repairDetailsColors.charactersText.light,
      primaryBG: buttonColors.primary.light,
      shopHomeFilterSelectedTextColor:
        shopHomeColors.filterSelectedTextColor.light,
      viewAllCTAIconColor: viewAllColors.CTAIcon.light,
      warrantyboxBorder: createFlowColors.formInputBorder.light,
      stepperBorder: createFlowColors.stepperBorder.light,
      background: createFlowColors.stepperBackground.light,
      sortIconColor: commonColors.sortIcon.light,
      selectedSortIconColor: commonColors.sortSelectedIcon.light,
    },
    dark: {
      pageBackground: createFlowColors.pageBackground.dark,
      breadcrumbText: createFlowColors.pageBreadcrumbText.dark,
      breadcrumbActiveText: createFlowColors.pageBreadcrumbActiveText.dark,
      separatorText: createFlowColors.pageSeparatorText.dark,
      formCardBackground: createFlowColors.formCardBackground.dark,
      formCardBorder: createFlowColors.formCardBorder.dark,
      formCardShadow: createFlowColors.formCardShadow.dark,
      formTitle: createFlowColors.formTitle.dark,
      formSubTitle: createFlowColors.formSubTitle.dark,
      formLabel: createFlowColors.formLabel.dark,
      addNoteText: createFlowColors.addNoteText.dark,
      warrantyText: createFlowColors.warrantyText.dark,
      formMandatory: createFlowColors.formMandatory.dark,
      formInputBG: createFlowColors.formInputBG.dark,
      formInputBorder: createFlowColors.formInputBorder.dark,
      formInputText: createFlowColors.formInputText.dark,
      formInputPlaceholder: createFlowColors.formInputPlaceholder.dark,
      verifyBtnDisabledBG: createFlowColors.verifyBtnDisabledBG.dark,
      verifyBtnDisabledText: createFlowColors.verifyBtnDisabledText.dark,
      verifyBtnEnabledBG: createFlowColors.verifyBtnEnabledBG.dark,
      verifyBtnEnabledText: createFlowColors.verifyBtnEnabledText.dark,
      verifyBtnBorder: createFlowColors.verifyBtnBorder.dark,
      cancelBtnBorder: createFlowColors.cancelBtnBorder.dark,
      cancelBtnText: createFlowColors.cancelBtnText.dark,
      cancelBtnBG: createFlowColors.cancelBtnBG.dark,
      nextBtnDisabledBG: createFlowColors.nextBtnDisabledBG.dark,
      nextBtnDisabledText: createFlowColors.nextBtnDisabledText.dark,
      nextBtnEnabledBG: createFlowColors.nextBtnEnabledBG.dark,
      nextBtnEnabledText: createFlowColors.nextBtnEnabledText.dark,
      footerBackground: createFlowColors.footerBackground.dark,
      footerBorder: createFlowColors.footerBorder.dark,
      // Asset Details Step 2
      assetDetailsSectionTitle: createFlowColors.assetDetailsSectionTitle.dark,
      assetDetailsDivider: createFlowColors.assetDetailsDivider.dark,
      assetDetailsLabelText: createFlowColors.assetDetailsLabelText.dark,
      assetDetailsValueText: createFlowColors.assetDetailsValueText.dark,
      assetDetailsImageBorder: createFlowColors.assetDetailsImageBorder.dark,
      openWorkOrdersBadgeBG: createFlowColors.openWorkOrdersBadgeBG.dark,
      openWorkOrdersBadgeText: createFlowColors.openWorkOrdersBadgeText.dark,
      openWorkOrdersBadgeBorder:
        createFlowColors.openWorkOrdersBadgeBorder.dark,
      tableHeaderBG: createFlowColors.tableHeaderBG.dark,
      tableHeaderText: createFlowColors.tableHeaderText.dark,
      tableRowText: createFlowColors.tableRowText.dark,
      tableRowBorder: createFlowColors.tableRowBorder.dark,
      tableNegativeDue: createFlowColors.tableNegativeDue.dark,
      priorityUrgentBG: createFlowColors.priorityUrgentBG.dark,
      priorityUrgentText: createFlowColors.priorityUrgentText.dark,
      priorityUrgentBorder: createFlowColors.priorityUrgentBorder.dark,
      priorityHighBG: createFlowColors.priorityHighBG.dark,
      priorityHighText: createFlowColors.priorityHighText.dark,
      priorityHighBorder: createFlowColors.priorityHighBorder.dark,
      priorityLowBG: createFlowColors.priorityLowBG.dark,
      priorityLowText: createFlowColors.priorityLowText.dark,
      priorityLowBorder: createFlowColors.priorityLowBorder.dark,
      expandRowBG: createFlowColors.expandRowBG.dark,
      expandRowBorder: createFlowColors.expandRowBorder.dark,
      expandIconColor: createFlowColors.expandIconColor.dark,
      expandDueDateText: createFlowColors.expandDueDateText.dark,
      // Contacts section
      contactTabActiveBG: createFlowColors.contactTabActiveBG.dark,
      contactTabActiveText: createFlowColors.contactTabActiveText.dark,
      contactTabActiveBadgeBG: createFlowColors.contactTabActiveBadgeBG.dark,
      contactTabActiveBadgeText:
        createFlowColors.contactTabActiveBadgeText.dark,
      contactTabInactiveBG: createFlowColors.contactTabInactiveBG.dark,
      contactTabInactiveText: createFlowColors.contactTabInactiveText.dark,
      contactTabInactiveBadgeBG:
        createFlowColors.contactTabInactiveBadgeBG.dark,
      contactTabInactiveBadgeText:
        createFlowColors.contactTabInactiveBadgeText.dark,
      contactTabBorder: createFlowColors.contactTabBorder.dark,
      contactCardRoleText: createFlowColors.contactCardRoleText.dark,
      contactCardNameText: createFlowColors.contactCardNameText.dark,
      contactCardInfoText: createFlowColors.contactCardInfoText.dark,
      listViewHeaderBG: listViewColors.headerBG.dark,
      listViewBorder: listViewColors.border.dark,
      listViewHeaderText: listViewColors.headerText.dark,
      workOrderStatusTextBG: workOrderColors.workOrderStatusTextBG.dark,
      workOrderStatusSelectedTextBG:
        workOrderColors.workOrderStatusSelectedTextBG.dark,
      tileCardCTASelectedBorder:
        repairDetailsColors.tileCardCTASelectedBorder.dark,
      tabTextColor: dashboardColors.tabTextColor.dark,
      selectedCountBG: dashboardColors.selecetdCountBG.dark,
      countBG: dashboardColors.countBG.dark,
      selectedCountTextColor: dashboardColors.selectedCountTextColor.dark,
      countTextColor: dashboardColors.countTextColor.dark,
      calenderBorder: workOrderColors.sortBorderAlt.dark,
      woTableHeaderText: createFlowColors.woTableHeaderText.dark,
      listBorder: listViewColors.lightBorder.dark,
      actionBG: createFlowColors.actionBG.dark,
      actionBorder: createFlowColors.actionBorder.dark,
      successTitle: createFlowColors.successTitle.dark,
      successSubTitle: createFlowColors.successSubTitle.dark,
      lightBorder: createFlowColors.lightBorder.dark,
      breadCrumbLight: createFlowColors.breadCrumbLight.dark,
      workOrderTileViewIcon: workOrderColors.tileViewIcon.dark,
      workOrdeViewBorder: shopHomeColors.workOrdeViewBorder.dark,
      workOrderListViewIconColor: workOrderColors.listViewIconColor.dark,
      workOrderTileViewSelectedIcon: workOrderColors.tileViewSelectedIcon.dark,
      workOrderListViewSelectedIconColor:
        workOrderColors.listViewSelectedIconColor.dark,
      workOrderSortIconsBorder: workOrderColors.sortBorderAlt.dark,
      // Card colors (mirror DashboardScreen)
      workOrderIdBGColor: workOrderColors.idBGColor.dark,
      workOrderIdTextColor: workOrderColors.idTextColor.dark,
      workOrderIdValueColor: workOrderColors.idValueColor.dark,
      tileViewBorder: tileViewColors.border.dark,
      listViewBG: listViewColors.BG.dark,
      descriptionTextColor: detailColors.descriptionTextColor.dark,
      repairIdTextColor: detailColors.repairIdTextColor.dark,
      repairIdValueColor: detailColors.repairIdValueColor.dark,
      cardSeparatorColor: detailColors.cardSeparator.dark,
      viewDetailsTextColor: detailColors.textColor.dark,
      detailLabelTextColor: detailColors.labelTextColor.dark,
      viewDetailsBorderColor: detailColors.borderColor.dark,
      ctaColor: detailColors.ctaColor.dark,
      inCompletedRepairs: statusColors.closedColorBG.dark,
      inCompletedRepairsText: statusColors.closedColorText.dark,
      selectedCTA: createFlowColors.selectedCTA.dark,
      selectCTAText: createFlowColors.selectedCTAText.dark,
      listSelectedBorder: checkboxColors.selectedBorderColor.dark,
      listUnSelectedBorder: checkboxColors.unSelectedBorderColor.dark,
      listSelectedBG: checkboxColors.selectedBGColor.dark,
      listUnSelectedBG: checkboxColors.unSelectedBGColor.dark,
      warrantyModalOverlay: createFlowColors.warrantyModalOverlay.dark,
      closeModalIconColor: repairDetailsColors.closeModalIconColor.dark,
      expandedWarrantyBG: createFlowColors.expandedWarrantyBG.dark,
      errorText: createFlowColors.errorText.dark,
      errorBG: createFlowColors.errorBG.dark,
      inputPlaceHolder: inputColors.placeholder.dark,
      meterInputBorder: createFlowColors.meterInputBorder.dark,
      workOrderDropdownPlaceholderText:
        workOrderColors.dropdownPlaceholderText.dark,
      calendarIcon: createFlowColors.calendarIcon.dark,
      charactersText: repairDetailsColors.charactersText.dark,
      primaryBG: buttonColors.primary.dark,
      shopHomeFilterSelectedTextColor:
        shopHomeColors.filterSelectedTextColor.dark,
      viewAllCTAIconColor: viewAllColors.CTAIcon.dark,
      warrantyboxBorder: createFlowColors.formInputBorder.dark,
      stepperBorder: createFlowColors.stepperBorder.dark,
      background: createFlowColors.stepperBackground.dark,
      sortIconColor: commonColors.sortIcon.dark,
      selectedSortIconColor: commonColors.sortSelectedIcon.dark,
    },
  },
};
